import React, { useState, useRef, useMemo, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query'; // ⭐ ADDED
import MapGL, { 
  GeolocateControl, 
  NavigationControl, 
  Marker,
  Source,
  Layer 
} from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  ChevronDownIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
// Components
import MapMarker from './MapMarker';
import { useNativeVenueLayer, venuesToGeoJSON } from './NativeVenueLayer'; // ✅ NEW: Native Mapbox layer
import DraggableCard from '../UI/DraggableCard';
import CategoryCarousel from '../UI/CategoryCarousel';
import FilterCarousel from '../UI/FilterCarousel';
// import OffersCarousel from '../UI/OffersCarousel';
import ZonesCarousel from '../UI/ZonesCarousel';
import LoadingSpinner from '../Common/LoadingSpinner';
import Header from '../Common/Header';
import 'mapbox-gl/dist/mapbox-gl.css';
import DiscoveryPage from '../Discovery/DiscoveryPage';
import AllEventsBanner from '../UI/AllEventsBanner';
import AllEventsPage from '../Events/AllEventsPage';
// Events removed - handled by EventsMapPage.jsx
import { REGION_CONFIG, getApiBaseUrl } from '../../config/region';
import { useVendorTracking } from '../../hooks/useVendorTracking';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'your-mapbox-token-here';

// ✅ DYNAMIC ZOOM CALCULATION based on screen size for perfect centering
const calculateDynamicZoom = (baseZoom, screenWidth, screenHeight) => {
  // Base zoom adjustments for different screen sizes
  let zoomAdjustment = 0;
  
  // Mobile phones (320px - 480px)
  if (screenWidth < 480) {
    zoomAdjustment = -1.5; // More zoom out for better overview on small screens
  } 
  // Small tablets (480px - 768px)
  else if (screenWidth < 768) {
    zoomAdjustment = -1; // Moderate zoom out
  } 
  // Tablets (768px - 1024px)
  else if (screenWidth < 1024) {
    zoomAdjustment = -0.5; // Slight zoom out
  } 
  // Small desktops (1024px - 1440px)
  else if (screenWidth < 1440) {
    zoomAdjustment = 0; // No adjustment
  } 
  // Large desktops (1440px+)
  else {
    zoomAdjustment = 0.5; // Slight zoom in for detail
  }
  
  // Adjust based on aspect ratio for better centering
  const aspectRatio = screenWidth / screenHeight;
  if (aspectRatio > 2.0) {
    // Ultra-wide screens - reduce zoom significantly
    zoomAdjustment -= 1;
  } else if (aspectRatio > 1.6) {
    // Wide screens - reduce zoom moderately
    zoomAdjustment -= 0.5;
  } else if (aspectRatio < 1.0) {
    // Portrait screens - increase zoom for better detail
    zoomAdjustment += 0.5;
  }
  
  // Special handling for very small screens
  if (screenHeight < 600) {
    zoomAdjustment -= 0.5; // Additional zoom out for short screens
  }
  
  return Math.max(9, Math.min(18, baseZoom + zoomAdjustment));
};

// ✅ ULTRA PERFORMANCE OPTIMIZATION CONSTANTS
// ✅ NEW: Simple throttle function for smooth performance
const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};
const MAX_MARKERS = 30; // Reduced from 50
const DEBOUNCE_DELAY = 8; // ✅ OPTIMIZED: Reduced to 8ms for 120fps smoothness
const CLUSTER_THRESHOLD = 30; // Reduced from 50
const ANIMATION_THROTTLE = 1; // Reduced from 8
const SMOOTH_ZOOM_DURATION = 400; // ✅ OPTIMIZED: Reduced to 400ms for faster response

// ✅ ULTRA-SMOOTH MAP SETTINGS for maximum performance and smoothness
const ULTRA_SMOOTH_MAP_SETTINGS = {
  // ✅ CORE PERFORMANCE SETTINGS
  antialias: true,
  preserveDrawingBuffer: false,
  cooperativeGestures: false,
  
  // ✅ ENHANCED ZOOM & PAN SETTINGS
  maxZoom: 20,
  minZoom: 8,
  touchZoomRotate: {
    around: 'center',
    momentum: true,
    deceleration: 0.99, // Increased from 0.985 for smoother deceleration
    threshold: 0.05 // Reduced from 0.1 for more responsive zoom
  },
  scrollZoom: {
    around: 'center',
    speed: 1.2, // Increased from 0.8 for faster zoom
    smooth: true,
    linearity: 0.1 // Add linearity for smoother zoom
  },
  boxZoom: true,
  dragRotate: false,
  dragPan: {
    around: 'center',
    linearity: 0.2, // Reduced from 0.3 for smoother panning
    deceleration: 3000, // Increased from 2500 for smoother stop
    maxSpeed: 1800, // Increased from 1400 for faster panning
    inertia: true // Enable inertia for smoother panning
  },
  keyboard: true,
  doubleClickZoom: {
    around: 'center'
  },
  
  // ✅ RENDERING OPTIMIZATIONS
  renderWorldCopies: false,
  trackResize: true,
  transitionInterpolator: undefined,
  transitionDuration: 0,
  optimizeForTerrain: true,
  fadeDuration: 0,
  crossSourceCollisions: false,
  
  // ✅ INTERACTION SETTINGS
  pitchWithRotate: false,
  touchPitch: false,
  maxPitch: 0, // Disable pitch for smoother zoom
  maxBearing: 0, // Disable bearing for smoother zoom
  
  // ✅ ADVANCED SMOOTHNESS
  touchZoomRotateThreshold: 0.05, // Reduced from 0.1
  scrollZoomThreshold: 0.05, // Reduced from 0.1
  dragPanThreshold: 0.05, // Reduced from 0.1
  
  // ✅ HARDWARE ACCELERATION
  preferWebGL2: true,
  failIfMajorPerformanceCaveat: false,
  
  // ✅ SMOOTH INTERPOLATION
  interpolationFactor: 0.15, // Increased from 0.1 for smoother transitions
  maxTileCacheSize: 100, // Increased from 50
  
  // ✅ REDUCE JANK
  refreshExpiredTiles: false,
  
  // ✅ ADD EASING FOR ZOOM
  easeTo: {
    duration: 300, // Fast zoom duration
    easing: t => t * (2 - t) // Smooth easing function
  }
};

const ZOOM_LEVELS = {
  WORLD_VIEW: 0,
  CONTINENT_VIEW: 2,
  COUNTRY_VIEW: 4,
  REGION_VIEW: 6,
  CITY_VIEW: 8,
  DISTRICT_VIEW: 10,
  NEIGHBORHOOD_VIEW: 12,
  STREET_VIEW: 14,
  BUILDING_VIEW: 16,
  PRECISE_LOCATION: 18,
  MAXIMUM_ZOOM: 20
};

// ✅ VENUE_CATEGORY_IMAGES removed - using native Mapbox icons instead (see NativeVenueLayer.js)

// ✅ MOSQUE CODE REMOVED - Dubai-specific, not relevant for Thailand/Phuket

// ✅ UTILITY: Extract coordinates from venue (handles all field variations)
const getVenueCoordinates = (venue) => {
  const lat = parseFloat(venue.lat);
  const lng = parseFloat(venue.lng);

  return { lat, lng, isValid: !isNaN(lat) && !isNaN(lng) && lat && lng };
};

// ✅ DISTANCE CALCULATION HELPER (for mosque finding)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// ✅ FIND NEAREST MOSQUE FUNCTION (OPTIMIZED)
const findNearestMosque = (venue, allVenues) => {
  const venueLat = venue.lat;
  const venueLng = venue.lng;
  
  if (!venueLat || !venueLng) return null;
  
  const mosques = allVenues.filter(v => 
    (v.venue_category === 'vc_religion_spiritual' || 
     v.venue_type === 'vt_mosque' || 
     v.venue_category_display === 'Religion & Spiritual') &&
    v.lat && v.lng
  );
  
  let nearestMosque = null;
  let minDistance = Infinity;
  
  mosques.forEach(mosque => {
    const mosqueLat = mosque.lat;
    const mosqueLng = mosque.lng;
    
    const distance = calculateDistance(venueLat, venueLng, mosqueLat, mosqueLng);
    
    if (distance < minDistance) {
      minDistance = distance;
      nearestMosque = { ...mosque, distance: distance };
    }
  });
  
  return nearestMosque;
};

// ✅ CHECK IF VENUE SHOULD SHOW MOSQUE INFO
const shouldShowMosqueInfo = (venue) => {
  const category = (venue.venue_category_display || venue.venue_type_display || '').toLowerCase();
  return category.includes('bar') || 
         category.includes('beach') || 
         category.includes('street vendor') ||
         category.includes('restaurant') ||
         category.includes('gym') ||
         category.includes('yoga') ||
         category.includes('club') ||
         category.includes('cafe') ||
         venue.venue_category === 'vc_bar' ||
         venue.venue_category === 'vc_street_vendor' ||
         venue.venue_category === 'vc_cafe' ||
         venue.venue_category === 'vc_restaurant' ||
         venue.venue_category === 'vc_gym' ||
         venue.venue_category === 'vc_yoga_studio' ||
         venue.venue_category === 'vc_beach_club';
};

// ✅ NEW: Fetch actual route from Mapbox Directions API
const fetchRouteToMosque = async (venueLat, venueLng, mosqueLat, mosqueLng, mapboxToken) => {
  try {
    const profile = 'walking';
    const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${venueLng},${venueLat};${mosqueLng},${mosqueLat}?geometries=geojson&access_token=${mapboxToken}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.routes && data.routes.length > 0) {
      const route = data.routes[0];
      return {
        geometry: route.geometry,
        distance: (route.distance / 1000).toFixed(2),
        duration: Math.round(route.duration / 60),
        mode: profile
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching route:', error);
    return null;
  }
};

// ✅ ULTRA-OPTIMIZED: MOSQUE MARKER COMPONENT (Only for Selected Venue)
const NearestMosqueMarker = memo(({ mosque, selectedVenue, allVenues, showPath = false }) => {
  const lat = parseFloat(mosque.lat);
  const lng = parseFloat(mosque.lng);
  
  if (!lat || !lng || isNaN(lat) || isNaN(lng)) return null;
  
  const selectedVenueData = allVenues.find(v => (v._id || v.Dubai_id) === selectedVenue);
  if (!selectedVenueData || !shouldShowMosqueInfo(selectedVenueData)) return null;
  
  const nearestMosque = findNearestMosque(selectedVenueData, allVenues);
  const isThisTheNearest = nearestMosque && (nearestMosque._id || nearestMosque.Dubai_id) === (mosque._id || mosque.Dubai_id);
  
  if (!isThisTheNearest) return null;
  
  return (
    <Marker longitude={lng} latitude={lat} anchor="center">
      <motion.div
        style={{
          width: '38px',
          height: '38px',
          backgroundColor: '#0ea5e9',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          border: '3px solid white',
          boxShadow: '0 6px 20px rgba(14, 165, 233, 0.5), 0 0 0 3px rgba(14, 165, 233, 0.2)',
          cursor: 'pointer',
          position: 'relative',
          zIndex: 1000
        }}
        whileHover={{ scale: 1.15 }}
        initial={{ scale: 0, rotate: -180 }}
        animate={{ 
          scale: 1, 
          rotate: 0,
          boxShadow: [
            '0 6px 20px rgba(14, 165, 233, 0.5), 0 0 0 3px rgba(14, 165, 233, 0.2)',
            '0 8px 25px rgba(14, 165, 233, 0.7), 0 0 0 6px rgba(14, 165, 233, 0.3)',
            '0 6px 20px rgba(14, 165, 233, 0.5), 0 0 0 3px rgba(14, 165, 233, 0.2)'
          ]
        }}
        exit={{ scale: 0, rotate: 180 }}
        transition={{ 
          type: "spring", 
          damping: 20, 
          stiffness: 300,
          boxShadow: { duration: 2, repeat: Infinity, ease: "easeInOut" }
        }}
      >
        🕌
      </motion.div>
    </Marker>
  );
});

NearestMosqueMarker.displayName = 'NearestMosqueMarker';

// Done by Yash
const DiscoverBanner = ({ onClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      onClick={onClick}
      style={{
        margin: '24px 0',
        position: 'relative',
        borderRadius: '20px',
        overflow: 'hidden',
        cursor: 'pointer',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '160px'
      }}
      whileHover={{ 
        scale: 1.02,
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18)',
        y: -4
      }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
        `,
        zIndex: 1
      }} />

      {/* Content */}
      <div style={{
        position: 'relative',
        zIndex: 2,
        padding: '24px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        {/* Top Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}>
              🔍
            </div>
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: 'white',
                margin: 0,
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                Discover
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.9)',
                margin: '4px 0 0 0',
                fontWeight: '500'
              }}>
                Explore hidden gems
              </p>
            </div>
          </div>
          
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            fontSize: '16px',
            color: 'white'
          }}>
            →
          </div>
        </div>

        {/* Bottom Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '6px 12px',
              borderRadius: '20px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <span style={{ fontSize: '12px' }}>⭐</span>
              <span style={{ 
                fontSize: '12px', 
                color: 'white', 
                fontWeight: '600' 
              }}>
                Curated
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '6px 12px',
              borderRadius: '20px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <span style={{ fontSize: '12px' }}>🎯</span>
              <span style={{ 
                fontSize: '12px', 
                color: 'white', 
                fontWeight: '600' 
              }}>
                Personalized
              </span>
            </div>
          </div>

          <div style={{
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.8)',
            fontWeight: '500',
            textAlign: 'right'
          }}>
            <div>Find your perfect</div>
            <div style={{ fontWeight: '700' }}>venue match</div>
          </div>
        </div>
      </div>

      {/* Shimmer Effect */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
        animation: 'shimmer 3s infinite',
        zIndex: 3
      }} />

      <style jsx>{`
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
      `}</style>
    </motion.div>
  );
};

const getValidCoordinates = (venue) => {
  if (!venue) return null;
  
  // Neon API: flat lat/lng
  const lat = parseFloat(venue.lat);
  const lng = parseFloat(venue.lng);
  
  // Enhanced validation
  if (
    isNaN(lat) || isNaN(lng) ||
    !isFinite(lat) || !isFinite(lng) ||
    lat < -90 || lat > 90 ||
    lng < -180 || lng > 180
  ) {
    return null;
  }
  
  return { lat, lng };
};

// ✅ UTILITY: Safe coordinate array creation
const createSafeCoordinateArray = (venue) => {
  const coords = getValidCoordinates(venue);
  return coords ? [coords.lng, coords.lat] : null;
};

// ===== STREET VENDOR MARKER COMPONENT =====
const StreetVendorMarker = memo(({ vendor, onSelect, isSelected, speedTier }) => {
  const lat = parseFloat(vendor.lat || vendor.latitude);
  const lng = parseFloat(vendor.lng || vendor.longitude);
  
  if (!lat || !lng || isNaN(lat) || isNaN(lng)) return null;
  
  return (
    <Marker longitude={lng} latitude={lat} anchor="center">
      <motion.div
        style={{
          width: '40px',
          height: '40px',
          backgroundColor: vendor.isOperational ? '#10b981' : '#ef4444',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          border: isSelected ? '3px solid #FFD700' : '2px solid white',
          boxShadow: isSelected 
            ? '0 0 15px rgba(255, 215, 0, 0.8)' 
            : '0 4px 12px rgba(0, 0, 0, 0.15)',
          cursor: 'pointer',
          position: 'relative',
          zIndex: isSelected ? 999 : 100
        }}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => onSelect(vendor._id)}
      >
        🚗
      </motion.div>
      
      {/* Operational status indicator */}
      {vendor.isOperational && (
        <motion.div
          style={{
            position: 'absolute',
            width: '8px',
            height: '8px',
            backgroundColor: '#10b981',
            borderRadius: '50%',
            bottom: '-2px',
            right: '-2px',
            border: '2px solid white'
          }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}
    </Marker>
  );
});

StreetVendorMarker.displayName = 'StreetVendorMarker';


const VenueMap = () => {
  const navigate = useNavigate();
  const mapRef = useRef();
  const debounceTimerRef = useRef(null);
  const prevZoomRef = useRef(null);
  const requestIdRef = useRef(null);
  const smoothZoomTimeoutRef = useRef(null);
  const [realtimeVendorLocations, setRealtimeVendorLocations] = useState(new Map());
  const [vendorTrails, setVendorTrails] = useState(new Map());

  // ✅ ADD THIS NEW CODE HERE (right after state declarations)
  const handleZoomLevel = useCallback((targetZoomLevel) => {
    if (!mapRef.current) return;
    
    const map = mapRef.current.getMap();
    const currentZoom = map.getZoom();
    const targetZoom = ZOOM_LEVELS[targetZoomLevel];
    
    map.flyTo({
      zoom: targetZoom,
      duration: 800,
      curve: 1.42,
      easing: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
      essential: true
    });
    
    console.log(`🔍 Zooming from ${currentZoom.toFixed(2)} to ${targetZoom}`);
  }, []);

  const zoomToWorldView = useCallback(() => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap();
    map.flyTo({
      center: [0, 20],
      zoom: ZOOM_LEVELS.WORLD_VIEW,
      bearing: 0,
      pitch: 0,
      duration: 1200,
      essential: true
    });
  }, []);

  const zoomToCompleteArea = useCallback((centerLng, centerLat) => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap();
    map.flyTo({
      center: [centerLng, centerLat],
      zoom: ZOOM_LEVELS.DISTRICT_VIEW,
      duration: 800,
      essential: true
    });
  }, []);

// ✅ FIXED CODE - Replace the entire fetchAllVenues function in VenueMap.jsx
const fetchAllVenues = useCallback(async () => {
  try {
    console.log('🗺️ Fetching all venues & vendors...');
    console.log('🌍 Current region:', REGION_CONFIG.region);
    
    // ✅ FIX: Use dynamic API URL from config
    const API_BASE_URL = getApiBaseUrl();
    
    // ✅ FIX: Check multiple possible token locations (optional in development)
    const token = localStorage.getItem('venue_app_token') || 
                  localStorage.getItem('token') || 
                  localStorage.getItem('authToken');
    
    if (!token) {
      console.warn('⚠️ No auth token found - proceeding without authentication (development mode)');
    } else {
      console.log('🔑 Using token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
    }

    // ✅ STEP 1: Fetch first page to get total pages
    const headers = {
      'Content-Type': 'application/json',
      'x-region': 'global'
    };

    // Only add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const firstVenueResponse = await fetch(`${API_BASE_URL}/venues?page=1&limit=1000`, {
      method: 'GET',
      headers
    });

    // ✅ FIX: Better error handling for 401
    if (firstVenueResponse.status === 401) {
      console.error('❌ Authentication failed - token may be expired');
      console.log('🔑 Token used:', token.substring(0, 50));
      return [];
    }

    if (!firstVenueResponse.ok) {
      console.error(`❌ Venues API error: ${firstVenueResponse.status} ${firstVenueResponse.statusText}`);
      const errorText = await firstVenueResponse.text();
      console.error('Error response:', errorText);
      return [];
    }

    const firstVenueData = await firstVenueResponse.json();
    let allVenues = firstVenueData.data || firstVenueData.venues || [];
    
    // ✅ STEP 2: Check if there are more pages
    const totalPages = firstVenueData.pagination?.totalPages || 1;
    const totalVenues = firstVenueData.pagination?.total || allVenues.length;
    
    console.log(`📊 Total venues: ${totalVenues}, Total pages: ${totalPages}`);
    console.log(`✅ Loaded page 1/${totalPages} (${allVenues.length} venues)`);

    // ✅ STEP 3: Fetch remaining pages in parallel
    if (totalPages > 1) {
      const remainingPagePromises = [];
      
      for (let page = 2; page <= totalPages; page++) {
        const pageHeaders = {
          'Content-Type': 'application/json',
          'x-region': 'global'
        };
        
        if (token) {
          pageHeaders['Authorization'] = `Bearer ${token}`;
        }
        
        remainingPagePromises.push(
          fetch(`${API_BASE_URL}/venues?page=${page}&limit=1000`, {
            method: 'GET',
            headers: pageHeaders
          })
          .then(async res => {
            if (res.status === 401) {
              console.error(`❌ Auth failed for page ${page}`);
              return [];
            }
            if (!res.ok) {
              console.error(`❌ Error fetching page ${page}: ${res.status}`);
              return [];
            }
            const data = await res.json();
            console.log(`✅ Loaded page ${page}/${totalPages}`);
            return data.data || data.venues || [];
          })
          .catch(err => {
            console.error(`❌ Error fetching page ${page}:`, err);
            return [];
          })
        );
      }

      // Wait for all pages to complete
      const remainingVenues = await Promise.all(remainingPagePromises);
      allVenues = [...allVenues, ...remainingVenues.flat()];
    }

    console.log(`✅ Loaded ${allVenues.length} venues from production API`);

    // ✅ STEP 4: Fetch street vendors (single page, usually small dataset)
    let vendors = [];
    try {
      const vendorHeaders = {
        'Content-Type': 'application/json',
        'x-region': REGION_CONFIG.region
      };
      
      if (token) {
        vendorHeaders['Authorization'] = `Bearer ${token}`;
      }
      
      const vendorsResponse = await fetch(`${API_BASE_URL}/street-vendors?page=1&limit=1000`, {
        method: 'GET',
        headers: vendorHeaders
      });

      if (vendorsResponse.ok) {
        const vendorsData = await vendorsResponse.json();
        vendors = vendorsData.data || vendorsData.vendors || [];
        console.log(`✅ Loaded ${vendors.length} street vendors`);
      } else if (vendorsResponse.status === 401) {
        console.error('❌ Auth failed for street vendors');
      }
    } catch (err) {
      console.warn('⚠️ Street vendors not available:', err.message);
    }

    // ✅ STEP 5: Map vendor data to venue format
    const vendorMarkersData = vendors.map(vendor => ({
      ...vendor,
      isStreetVendor: true,
      venueType: 'streetvendor',
      venue_type_display: 'Street Vendor',
      venue_category_display: 'Street Vendor',
      name: vendor.vendorName || vendor.name,
      lat: vendor.currentLocation?.coordinates?.[1] || vendor.latitude,
      lng: vendor.currentLocation?.coordinates?.[0] || vendor.longitude,
    }));

    const allData = [...allVenues, ...vendorMarkersData];
    console.log(`✅ Total venues + vendors: ${allData.length}`);
    
    return allData;
    
  } catch (error) {
    console.error('❌ Error fetching venues & vendors:', error);
    console.error('Error stack:', error.stack);
    return [];
  }
}, []);

  // ===== USE QUERY - AT TOP LEVEL OF COMPONENT =====
const { data: venues = [], isLoading, error } = useQuery({
  queryKey: ['venues-vendors', REGION_CONFIG.region], // ✅ ADDED: Include region
  queryFn: fetchAllVenues,
  staleTime: 10 * 60 * 1000,
  cacheTime: 30 * 60 * 1000,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: 1, // ✅ ADDED: Only retry once on failure
  onError: (error) => {
    console.error('❌ Failed to fetch venues:', error.message);
  }
});

  // ✅ REMOVED: Events data (handled by separate EventsMapPage.jsx)
  const eventsData = null;
  const eventsLoading = false;
  const eventsError = null;

const [pagination, setPagination] = useState(null);
const shouldKeepDrawerOpenRef = useRef(false);
const [shouldKeepDrawerOpen, setShouldKeepDrawerOpen] = useState(false);


// ✅ Reset the flag after 1 second
useEffect(() => {
  if (shouldKeepDrawerOpen) {
    const timer = setTimeout(() => {
      setShouldKeepDrawerOpen(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }
}, [shouldKeepDrawerOpen]);


  // ✅ REMOVED: totalEventsCount (not needed on venue map)
  const totalEventsCount = 0;

  // ✅ REMOVED: venuesWithEvents (not needed on venue map)
  const venuesWithEvents = new Set();

  // ✅ REMOVED: Events filters (not needed on venue map)
  const eventsFilters = {
    searchQuery: '',
    selectedCategory: '',
    selectedSubtype: '',
    startDate: '',
    endDate: ''
  };

  // ✅ REMOVED: venuesEventTypes (not needed on venue map)
  const venuesEventTypes = new Map();

  // ✅ REMOVED: venuesEventTimes (not needed on venue map)
  const venuesEventTimes = new Map();

  // ✅ REMOVED: venuesEventPrices (not needed on venue map)
  const venuesEventPrices = new Map();

// ✅ ENHANCED: Screen-size aware initial map state with optimized settings
const getInitialZoom = () => {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  
  if (screenWidth < 768) {
    return 10;
  } else if (screenWidth < 1024) {
    return 11;
  } else {
    return 11.5;
  }
};

const [viewState, setViewState] = useState({
  longitude: 98.3923,  // ✅ FIXED: Phuket longitude (was Bangkok 100.5018)
  latitude: 7.8804,    // ✅ FIXED: Phuket latitude (was Bangkok 13.7563)
  zoom: 12,            // ✅ Slightly zoomed in for Phuket
  minZoom: ZOOM_LEVELS.WORLDVIEW,
  maxZoom: ZOOM_LEVELS.MAXIMUMZOOM
});

const zoomToExactLocation = useCallback((latitude, longitude, zoomLevel = ZOOM_LEVELS.PRECISE_LOCATION) => {
  if (!mapRef.current || !latitude || !longitude) return;
  
  const map = mapRef.current.getMap();
  map.flyTo({
    center: [longitude, latitude],
    zoom: zoomLevel,
    duration: 1000,
    curve: 1.42,
    easing: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
    essential: true,
    padding: { top: 50, bottom: 200, left: 50, right: 50 }
  });
}, []);

// ✅ FIXED: Remove filteredVenues dependency - search your venue API directly
const handleSearchAndZoom = useCallback(async (query) => {
  if (!query.trim() || !mapRef.current) return;
  
  try {
    // First, search in your venues data for matching venue
    const matchingVenue = venues.find(v => 
      v.name?.toLowerCase().includes(query.toLowerCase()) ||
      v.vendorName?.toLowerCase().includes(query.toLowerCase()) ||
      v.city?.toLowerCase().includes(query.toLowerCase())
    );
    
    if (matchingVenue) {
      // Zoom to the matching venue
      const lat = parseFloat(matchingVenue.lat);
      const lng = parseFloat(matchingVenue.lng);
      
      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        console.log('🔍 Found venue:', matchingVenue.name || matchingVenue.vendorName, 'at', [lng, lat]);
        zoomToExactLocation(lat, lng, ZOOM_LEVELS.STREET_VIEW);
        setSelectedVenue(matchingVenue._id || matchingVenue.Dubai_id);
        return { lat, lng, venueName: matchingVenue.name || matchingVenue.vendorName };
      }
    }
    
    // If no venue found, use Mapbox Geocoding
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
      `access_token=${MAPBOX_TOKEN}&` +
      `limit=1&` +
      `proximity=${viewState.longitude},${viewState.latitude}`
    );
    
    const data = await response.json();
    
    if (data.features && data.features.length > 0) {
      const [lng, lat] = data.features[0].center;
      const placeName = data.features[0].place_name;
      
      console.log('🔍 Geocoded location:', placeName, 'at', [lng, lat]);
      zoomToExactLocation(lat, lng, ZOOM_LEVELS.NEIGHBORHOOD_VIEW);
      
      return { lat, lng, placeName };
    }
  } catch (error) {
    console.error('❌ Search error:', error);
  }
}, [venues, Math.floor(viewState.longitude * 100) / 100, Math.floor(viewState.latitude * 100) / 100, zoomToExactLocation]); // ✅ OPTIMIZED: Round coordinates to 2 decimals

useEffect(() => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('User location detected:', position.coords);
        setViewState(prev => ({
          ...prev,
          longitude: position.coords.longitude,
          latitude: position.coords.latitude,
          zoom: 13 // Zoom in when user location is detected
        }));
      },
      (error) => {
        console.log('Geolocation error, using Phuket location:', error);
        // ✅ FIXED: Fallback to Phuket (not Bangkok)
        setViewState(prev => ({
          ...prev,
          longitude: 98.3923,  // Phuket
          latitude: 7.8804,     // Phuket
          zoom: 12
        }));
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  } else {
    console.log('Geolocation not supported, using Phuket location');
    // ✅ FIXED: Default to Phuket
    setViewState(prev => ({
      ...prev,
      longitude: 98.3923,
      latitude: 7.8804,
      zoom: 12
    }));
  }
}, []);

  const [showFilters, setShowFilters] = useState(false);
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v11');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [showWifiBadges, setShowWifiBadges] = useState(false);
  const [showBudgetBadges, setShowBudgetBadges] = useState(false);
  const [showRatingsBadges, setShowRatingsBadges] = useState(false);
  const [showTimeBadges, setShowTimeBadges] = useState(false);
  const [showPaymentBadges, setShowPaymentBadges] = useState(false);
  const [draggableCardHeight, setDraggableCardHeight] = useState(380); // ✅ FIXED: Match initialHeight from DraggableCard
  const [showMosquePath, setShowMosquePath] = useState(false);
  const [mosqueRoute, setMosqueRoute] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isZooming, setIsZooming] = useState(false); // ✅ ADD: Track zoom operations

  const [screenSize, setScreenSize] = useState({
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024
  });

  const mapStyles = [
    { name: 'Streets', value: 'mapbox://styles/mapbox/streets-v11', icon: '🗺️' },
    { name: 'Satellite', value: 'mapbox://styles/mapbox/satellite-streets-v11', icon: '🛰️' },
    { name: 'Light', value: 'mapbox://styles/mapbox/light-v10', icon: '☀️' },
    { name: 'Dark', value: 'mapbox://styles/mapbox/dark-v10', icon: '🌙' }
  ];
  
  // ✅ Clean map: Hide POI labels to avoid duplicating with our markers
  useEffect(() => {
    if (!mapRef.current || !isMapLoaded) return;
    
    const map = mapRef.current.getMap();
    if (!map) return;
    
    // Wait for style to load, then hide labels
    const hideLabels = () => {
      const style = map.getStyle();
      if (!style || !style.layers) return;
      
      // Hide POI labels AND place labels for a cleaner look
      const labelsToHide = [
        // POI labels (restaurants, cafes, shops, etc.)
        'poi-label',
        'poi',
        // Place labels (cities, towns, neighborhoods)
        'place-label',
        'place-city-label',
        'place-town-label',
        'place-village-label',
        'place-suburb-label',
        'place-neighborhood-label',
        'settlement-label',
        'settlement-subdivision-label',
        // Transit
        'transit-label',
        'transit',
        // Other labels that might clutter
        'airport-label',
        'natural-point-label',
        'water-point-label',
        'waterway-label',
      ];
      
      let hiddenCount = 0;
      style.layers.forEach(layer => {
        // Check if layer ID contains any of our patterns
        const shouldHide = labelsToHide.some(pattern => 
          layer.id.toLowerCase().includes(pattern.toLowerCase())
        );
        
        if (shouldHide) {
          try {
            map.setLayoutProperty(layer.id, 'visibility', 'none');
            hiddenCount++;
          } catch (e) {
            // Layer might not exist in this style
          }
        }
      });
      
      console.log('🧹 Hidden', hiddenCount, 'label layers for cleaner map');
    };
    
    // Run on style load and when style changes
    map.on('style.load', hideLabels);
    
    // Run after a short delay to ensure style is fully loaded
    setTimeout(hideLabels, 500);
    
    return () => {
      map.off('style.load', hideLabels);
    };
  }, [isMapLoaded, mapStyle]);

  // WM WiFi slugs state (fetched on mount)
  const [wmWifiSlugs, setWmWifiSlugs] = useState(new Set());

  useEffect(() => {
    fetch('/api/wifi/wm-venue-slugs')
      .then(r => r.json())
      .then(d => { if (d.slugs) setWmWifiSlugs(new Set(d.slugs)); })
      .catch(() => {}); // silent fail — filter just won't show pink
  }, []);

  // Filters state
  const [filters, setFilters] = useState({
    // WiFi filters
    hasWifi: false,
    hasWifiVerified: false,
    hasWifiUnverified: false,
    hasWifiGold: false,
    hasWifiSilver: false,
    hasWifiBronze: false,
    hasCharging: false,
    hasWifiSpeed: false,
    hasWifiWM: false,
    
    // Noise filters
    isQuiet: false,
    isModerate: false,
    isLively: false,
    
    // Price level filters
    priceLevel1: false,
    priceLevel2: false,
    priceLevel3: false,
    priceLevel4: false,
    
    // Payment filters
    paymentCash: false,
    paymentCard: false,
    paymentCrypto: false,
    
    // Rating filters
    rating3: false,
    rating35: false,
    rating4: false,
    rating45: false,
    rating5: false,
    
    // AC/Fan filters
    acFanAC: false,
    acFanFan: false,
    
    // Entry Fees filters
    entryFeesYes: false,
    entryFeesNo: false,
    
    // Breakfast filters
    breakfastYes: false,
    breakfastNo: false,
    
    // Brunch filters
    brunchYes: false,
    brunchNo: false,
    
    // Dinner filters
    dinnerYes: false,
    dinnerNo: false,
    
    // Open Late filters
    openLateYes: false,
    openLateNo: false,
    
    // Take Booking filters
    takeBookingYes: false,
    takeBookingNo: false,
    
    // Parking filters
    parkingMall: false,
    parkingValet: false,
    parkingStreet: false,
    parkingBasement: false,
    parkingLot: false,
    parkingNone: false,
    
    // Family Friendly filters
    familyScore1: false,
    familyScore2: false,
    familyScore3: false,
    familyScore4: false,
    familyScore5: false,
    
    // View filters
    viewStreet: false,
    viewWaterfront: false,
    viewPark: false,
    viewAinView: false,
    viewMarinaSkyline: false,
    viewSunset: false,
    viewBeachView: false,
    viewCourtyard: false,
    viewSouq: false,
    viewBoardwalk: false,
    
    // Category filters
    venueCategory: [],
    venueSubType: [],
    cuisineTags: [],
    dietaryTags: [],
    groupPolicyTags: [],
    healthyFoodLevels: [],
    
    // Other filters
    openLate: false,
    vegOnly: false,
    outdoorSeating: false,
    minRating: 0,
    Mosque: false,
    highRated: false,
    petPolicy: [], // ✅ ADD THIS
    breakfastOffered: false, // ✅ ADD THIS
    paymentCard: false, // ✅ ADD THIS
    paymentCrypto: false,
    venuecategory: [], // ✅ ADD THIS
    Nearby: false,
    Offers: false
  });
  
    // ✅ Sync ref with state
  useEffect(() => {
    shouldKeepDrawerOpenRef.current = shouldKeepDrawerOpen;
  }, [shouldKeepDrawerOpen]);

  // ✅ Reset the flag after 1 second
  useEffect(() => {
    if (shouldKeepDrawerOpen) {
      const timer = setTimeout(() => {
        setShouldKeepDrawerOpen(false);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [shouldKeepDrawerOpen]);

  const [selectedPrimaryCategory, setSelectedPrimaryCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [visibleVenues, setVisibleVenues] = useState([]);

  // ✅ NEW: Add discovery state
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [isLoadingDiscovery, setIsLoadingDiscovery] = useState(false);

  // Add these state variables after the existing discovery states
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [isLoadingAllEvents, setIsLoadingAllEvents] = useState(false);
  // ✅ REMOVED: Events mode (handled by EventsMapPage.jsx)
  const isEventsMode = false;
  const setIsEventsMode = () => {}; // No-op
  
  // ✅ NEW: Use native Mapbox layer for better performance
  const USE_NATIVE_LAYER = true; // Toggle between native layer and React markers

  // ✅ OPTIMIZED: Only recalculate on significant zoom changes (not every pan/zoom)
  const actualVisibleMarkersCount = useMemo(() => {
  if (!mapRef.current || !isMapLoaded) return 0;
  
  try {
    const map = mapRef.current.getMap();
    const bounds = map.getBounds();
    
    // Count markers that are actually within current viewport
    const markersInView = visibleVenues.filter(venue => {
      const { lat, lng, isValid } = getVenueCoordinates(venue);
      if (!isValid) return false;
      
      return lat >= bounds._sw.lat && 
             lat <= bounds._ne.lat && 
             lng >= bounds._sw.lng && 
             lng <= bounds._ne.lng;
    });
    
    return markersInView.length;
  } catch (error) {
    return visibleVenues.length;
  }
}, [visibleVenues, Math.floor(viewState.zoom), isMapLoaded]); // Only when zoom level changes (rounded)

  // ✅ ENHANCED: Fetch route when mosque is selected
  const selectedVenueNearestMosque = useMemo(() => {
    if (!selectedVenue || !venues.length) return null;
    
    const selectedVenueData = venues.find(v => (v._id || v.Dubai_id) === selectedVenue);
    if (!selectedVenueData || !shouldShowMosqueInfo(selectedVenueData)) return null;
    
    return findNearestMosque(selectedVenueData, venues);
  }, [selectedVenue, venues]);

  // ✅ NEW: Fetch mosque route when selection changes
  useEffect(() => {
    const fetchMosqueRoute = async () => {
      if (!selectedVenue || !venues.length) {
        setMosqueRoute(null);
        return;
      }
      
      const selectedVenueData = venues.find(v => (v._id || v.Dubai_id) === selectedVenue);
      if (!selectedVenueData || !shouldShowMosqueInfo(selectedVenueData)) {
        setMosqueRoute(null);
        return;
      }
      
      const nearestMosque = findNearestMosque(selectedVenueData, venues);
      if (!nearestMosque) {
        setMosqueRoute(null);
        return;
      }
      
      const venueLat = selectedVenueData.lat;
      const venueLng = selectedVenueData.lng;
      const mosqueLat = nearestMosque.lat;
      const mosqueLng = nearestMosque.lng;
      
      const route = await fetchRouteToMosque(
        parseFloat(venueLat),
        parseFloat(venueLng),
        parseFloat(mosqueLat),
        parseFloat(mosqueLng),
        MAPBOX_TOKEN
      );
      
      setMosqueRoute(route);
    };
    
    fetchMosqueRoute();
  }, [selectedVenue, venues]);
  
  // ✅ ULTRA-OPTIMIZATION: Filter out mosque venues from regular display
  const nonMosqueVenues = useMemo(() => {
    return venues.filter(venue => 
      venue.venue_category !== 'vc_religion_spiritual' && 
      venue.venue_type !== 'vt_mosque' && 
      venue.venue_category_display !== 'Religion & Spiritual'
    );
  }, [venues]);

  const streetVendorIds = useMemo(() => {
  return venues
    .filter(v => v.isStreetVendor)
    .map(v => v._id || v.Dubai_id)
    .filter(Boolean);
}, [venues]);

// ===== STEP 2: Handler Functions - MOVE OUTSIDE useMemo =====
const handleVendorLocationUpdate = useCallback((data) => {
  console.log('📍 Real-time location update:', data);
  
  setRealtimeVendorLocations(prev => {
    const updated = new Map(prev);
    updated.set(data.vendorId, {
      coordinates: data.coordinates,
      latitude: data.latitude,
      longitude: data.longitude,
      timestamp: new Date(data.timestamp),
      accuracy: data.accuracy,
      isOperational: data.isOperational
    });
    return updated;
  });

  // ✅ ADD THIS: Update vendor trail history
  setVendorTrails(prev => {
    const updated = new Map(prev);
    const existingTrail = updated.get(data.vendorId) || [];
    
    // Add new location to trail
    const newTrail = [
      ...existingTrail,
      {
        coordinates: data.coordinates,
        timestamp: data.timestamp,
        accuracy: data.accuracy
      }
    ];
    
    // Keep last 24 hours of data
    const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
    const filteredTrail = newTrail.filter(loc => 
      new Date(loc.timestamp).getTime() > twentyFourHoursAgo
    );
    
    updated.set(data.vendorId, filteredTrail);
    return updated;
  });
}, []);

const handleVendorStatusChange = useCallback((data) => {
  console.log('🔄 Vendor status changed:', data);
  
  setVisibleVenues(prevVenues => 
    prevVenues.map(venue => {
      if ((venue._id || venue.Dubai_id) === data.vendorId) {
        return {
          ...venue,
          isOperational: data.isOperational
        };
      }
      return venue;
    })
  );
}, []);

// ===== STEP 3: Initialize Tracking Hook - MOVE OUTSIDE useMemo =====
useVendorTracking(
  streetVendorIds, 
  handleVendorLocationUpdate,
  handleVendorStatusChange
);

// ===== STEP 4: Update Effect - MOVE OUTSIDE useMemo =====
useEffect(() => {
  if (realtimeVendorLocations.size === 0) return;

  setVisibleVenues(prevVisible => {
    return prevVisible.map(venue => {
      if (!venue.isStreetVendor) return venue;

      const vendorId = venue.id || venue.Dubaiid;
      const realtimeData = realtimeVendorLocations.get(vendorId);
      // ✅ ADD THIS: Get trail data
      const trailData = vendorTrails.get(vendorId);

      if (realtimeData) {
        return {
          ...venue,
          lat: realtimeData.latitude,
          lng: realtimeData.longitude,
          isOperational: realtimeData.isOperational,
          lastLocationUpdate: realtimeData.timestamp,
          // ✅ ADD THIS: Include complete trail history
          locationHistory: trailData || []
        };
      }
      return venue;
    });
  });
}, [realtimeVendorLocations, vendorTrails]);

const filteredVenues = useMemo(() => {
  // Reset debug counter
  window.wifiSpeedDebugCount = 0;
  
  if (!nonMosqueVenues || !Array.isArray(nonMosqueVenues)) {
    return [];
  }
  
  const filterStartTime = performance.now();
  
  // ✅ CRITICAL: Check if ANY filters are active
  const hasActiveFilters = 
    (Array.isArray(filters.venueGroup) && filters.venueGroup.length > 0) ||
    (Array.isArray(filters.venueCategory) && filters.venueCategory.length > 0) ||
    (Array.isArray(filters.venueSubType) && filters.venueSubType.length > 0) ||
    (Array.isArray(filters.cuisineTags) && filters.cuisineTags.length > 0) ||
    (Array.isArray(filters.dietaryTags) && filters.dietaryTags.length > 0) ||
    (Array.isArray(filters.groupPolicyTags) && filters.groupPolicyTags.length > 0) ||
    (Array.isArray(filters.healthyFoodLevels) && filters.healthyFoodLevels.length > 0) ||
    filters.hasWifi || filters.hasWifiVerified || filters.hasWifiUnverified ||
    filters.hasWifiGold || filters.hasWifiSilver || filters.hasWifiBronze ||
    filters.hasWifiSpeed || filters.hasWifiWM || // ✅ ADDED: WiFi Speed + WM filter check
    filters.hasCharging || filters.isQuiet || filters.isModerate || filters.isLively ||
    filters.priceLevel1 || filters.priceLevel2 || filters.priceLevel3 || filters.priceLevel4 ||
    filters.paymentCash || filters.paymentCard || filters.paymentCrypto ||
    filters.openLate || filters.vegOnly || filters.outdoorSeating ||
    filters.rating3 || filters.rating35 || filters.rating4 || filters.rating45 || filters.rating5 ||
    filters.acFanAC || filters.acFanFan || filters.entryFeesYes || filters.entryFeesNo ||
    filters.breakfastYes || filters.breakfastNo || filters.brunchYes || filters.brunchNo ||
    filters.dinnerYes || filters.dinnerNo || filters.openLateYes || filters.openLateNo ||
    filters.takeBookingYes || filters.takeBookingNo ||
    filters.parkingMall || filters.parkingValet || filters.parkingStreet || filters.parkingBasement || filters.parkingLot || filters.parkingNone ||
    filters.familyScore1 || filters.familyScore2 || filters.familyScore3 || filters.familyScore4 || filters.familyScore5 ||
    filters.viewStreet || filters.viewWaterfront || filters.viewPark || filters.viewAinView ||
    filters.viewMarinaSkyline || filters.viewSunset || filters.viewBeachView ||
    filters.viewCourtyard || filters.viewSouq || filters.viewBoardwalk ||
    filters.highRated || filters.petFriendly || filters.Nearby || filters.Offers || filters.Mosque ||
    (searchQuery && searchQuery.trim().length > 0);

  // ✅ FIX: If NO filters are active, return ALL venues with valid coordinates
  if (!hasActiveFilters && !isEventsMode) {
    const validVenues = nonMosqueVenues.filter(venue => {
      const { lat, lng, isValid } = getVenueCoordinates(venue);
      return isValid;
    });
    
    console.log(`✅ No filters - returning all ${validVenues.length} venues`);
    return validVenues;
  }

  // ✅ Start performance timing for filtering
  let filtered = nonMosqueVenues.filter(venue => {
    const venueName = venue.name || venue.name;
    if (!venueName) return false;
    
    // ✅ OPTIMIZED: Extract coordinates once using utility
    const { lat, lng, isValid } = getVenueCoordinates(venue);
    if (!isValid) return false;

    // ✅ LEVEL 1: GROUP FILTER (most restrictive first for early exit)
    if (Array.isArray(filters.venueGroup) && filters.venueGroup.length > 0) {
      const venueGroupId = venue.groupid || venue.group_id;
      const venueGroupDisplay = venue.groupiddisplayname || venue.group_id_display_name;
      
      const groupMatches = filters.venueGroup.some(selectedGroup => 
        selectedGroup === venueGroupId || 
        selectedGroup === venueGroupDisplay ||
        venueGroupDisplay?.toLowerCase() === selectedGroup.toLowerCase()
      );
      
      if (!groupMatches) return false; // ✅ EARLY EXIT
    }

    // ✅ LEVEL 2: CATEGORY FILTER
    if (Array.isArray(filters.venueCategory) && filters.venueCategory.length > 0) {
      if (venue.isStreetVendor) {
        const hasStreetVendorFilter = filters.venueCategory.some(cat => 
          cat.toLowerCase().includes('street') || 
          cat === 'street_vendor' || 
          cat === 'streetvendor'
        );
        if (!hasStreetVendorFilter) return false;
      } else {
        const venueCategoryId = venue.venue_category || venue.venuecategory;
        const venueCategoryDisplay = venue.venue_category_display || venue.venuecategorydisplayname;
        
        const categoryMatches = filters.venueCategory.some(selectedCategory =>
          selectedCategory === venueCategoryId ||
          selectedCategory === venueCategoryDisplay ||
          venueCategoryDisplay?.toLowerCase() === selectedCategory.toLowerCase()
        );
        
        if (!categoryMatches) return false;
      }
    }
    
    // ✅ LEVEL 3: VENUE TYPE DISPLAY FILTER
    if (Array.isArray(filters.venueSubType) && filters.venueSubType.length > 0) {
      if (venue.isStreetVendor) {
        const parentCategoryMatches = filters.venueCategory?.length === 0 || 
          filters.venueCategory?.some(cat => 
            cat.toLowerCase().includes('street') || 
            cat === 'street_vendor' || 
            cat === 'streetvendor'
          );
        if (!parentCategoryMatches) return false;
      } else {
        const venueTypeId = venue.venue_type || venue.venuetype;
        const venueTypeDisplay = venue.venue_type_display || venue.venuetypedisplay;
        
        const typeMatches = filters.venueSubType.some(selectedType =>
          selectedType === venueTypeId ||
          selectedType === venueTypeDisplay ||
          venueTypeDisplay?.toLowerCase() === selectedType.toLowerCase()
        );
        
        if (!typeMatches) return false;
      }
    }

    // ✅ LEVEL 4: QUICK FILTERS

    // ✅ Cuisine tags filter
    if (Array.isArray(filters.cuisineTags) && filters.cuisineTags.length > 0) {
      const venueCuisineTags = (venue.Cuisine_Tags || venue.cuisineTags || '')
        .toString()
        .split(/[,;]/)
        .map(tag => tag.trim().toLowerCase())
        .filter(tag => tag);
      
      const hasMatchingCuisine = filters.cuisineTags.some(selectedTag => {
        const selectedTagLower = selectedTag.toLowerCase();
        return venueCuisineTags.some(venueTag => 
          venueTag.includes(selectedTagLower) || 
          selectedTagLower.includes(venueTag) ||
          venueTag === selectedTagLower
        );
      });
      
      if (!hasMatchingCuisine) return false;
    }

    // ✅ Dietary tags filter
    if (Array.isArray(filters.dietaryTags) && filters.dietaryTags.length > 0) {
      const venueDietaryTags = (venue.Dietary_tags || '')
        .toString()
        .split(/[,;]/)
        .map(tag => tag.trim().toLowerCase())
        .filter(tag => tag);
      
      const hasMatchingDietary = filters.dietaryTags.some(selectedTag => {
        const selectedTagLower = selectedTag.toLowerCase();
        return venueDietaryTags.some(venueTag => 
          venueTag.includes(selectedTagLower) || 
          selectedTagLower.includes(venueTag) ||
          venueTag === selectedTagLower
        );
      });
      
      if (!hasMatchingDietary) return false;
    }

    // ✅ Group policy tags filter
    if (Array.isArray(filters.groupPolicyTags) && filters.groupPolicyTags.length > 0) {
      const venueGroupPolicies = (venue.Group_policy || '')
        .toString()
        .split(/[,;]/)
        .map(policy => policy.trim().toLowerCase())
        .filter(policy => policy);
      
      const hasMatchingGroupPolicy = filters.groupPolicyTags.some(selectedTag => {
        const selectedTagLower = selectedTag.toLowerCase();
        return venueGroupPolicies.some(venueTag => 
          venueTag.includes(selectedTagLower) || 
          selectedTagLower.includes(venueTag) ||
          venueTag === selectedTagLower
        );
      });
      
      if (!hasMatchingGroupPolicy) return false;
    }

    // ✅ Healthy food levels filter
    if (Array.isArray(filters.healthyFoodLevels) && filters.healthyFoodLevels.length > 0) {
      if (!venue.Healthy_food_level) return false;
      
      const venueHealthyLevel = venue.Healthy_food_level.toLowerCase();
      const hasMatchingHealthyLevel = filters.healthyFoodLevels.some(selectedLevel => 
        venueHealthyLevel === selectedLevel.toLowerCase()
      );
      
      if (!hasMatchingHealthyLevel) return false;
    }

    // ✅ Payment types filter
    if (filters.paymentCash || filters.paymentCard || filters.paymentCrypto) {
      const venuePayments = (venue["Payment types"] || venue.paymentTypes || venue.Payment_Cash || venue.Payment_Card || venue.Payment_Crypto || '').toString().toLowerCase();
      
      if (filters.paymentCash && !venuePayments.includes('cash') && !venue.Payment_Cash) return false;
      if (filters.paymentCard && !venuePayments.includes('card') && !venue.Payment_Card) return false;
      if (filters.paymentCrypto && !venuePayments.includes('crypto') && !venue.Payment_Crypto) return false;
    }

    // ✅ Price level filters
    if (filters.priceLevel1 || filters.priceLevel2 || filters.priceLevel3 || filters.priceLevel4) {
      const venueLevel = venue.HL_Price_Level ? parseInt(venue.HL_Price_Level) :
        (venue.priceRange === '$' ? 1 : venue.priceRange === '$$' ? 2 : venue.priceRange === '$$$' ? 3 : venue.priceRange === '$$$$' ? 4 : null);
      
      if (filters.priceLevel1 && venueLevel !== 1) return false;
      if (filters.priceLevel2 && venueLevel !== 2) return false;
      if (filters.priceLevel3 && venueLevel !== 3) return false;
      if (filters.priceLevel4 && venueLevel !== 4) return false;
    }

    // ✅ WiFi filters - ALL variations handled
    if (filters.hasWifi) {
      const hasWifi = venue.PubWifi === true || venue.PubWifi === 1 || 
                      venue.Amenities?.wifi === true ||
                      venue.wifi === true || venue.PubWifi === "true" || venue.PubWifi === "1";
      if (!hasWifi) return false;
    }

// ✅ CORRECT WiFi Speed filter - FIXED to use hasWifiSpeedData
if (filters.hasWifiSpeed) {
  // Check all possible speed field variations
  const dlSpeed = parseFloat(
    venue.DLSPeedMBPS || 
    venue.DLSpeedMBPS || 
    venue.dlSpeedMbps || 
    0
  );
  const ulSpeed = parseFloat(
    venue.ULSPeedMBPS || 
    venue.ULSpeedMBPS || 
    venue.ulSpeedMbps || 
    0
  );
  
  // ✅ CRITICAL FIX: Backend returns hasWifiSpeedData, not hasWifiTests
  const hasWifiData = venue.hasWifiSpeedData === true;
  
  // Venue must have WiFi speed data
  if (!hasWifiData || (dlSpeed === 0 && ulSpeed === 0)) {
    return false;
  }
}

    if (filters.hasWifiVerified) {
      const hasWifi = venue.PubWifi === true || venue.PubWifi === 1 || venue.Pub_Wifi === true || venue.Pub_Wifi === 1;
      if (!hasWifi || (venue.Wifibage !== 'Verified' && venue.Wifi_bage !== 'Verified')) return false;
    }

    if (filters.hasWifiUnverified) {
      const hasWifi = venue.PubWifi === true || venue.PubWifi === 1 || venue.Pub_Wifi === true || venue.Pub_Wifi === 1;
      if (!hasWifi || (venue.Wifibage === 'Verified' || venue.Wifi_bage === 'Verified')) return false;
    }

    if (filters.hasWifiGold) {
      const hasWifi = venue.PubWifi === true || venue.PubWifi === 1 || venue.Pub_Wifi === true || venue.Pub_Wifi === 1;
      const speed = parseFloat(venue.DLSPeedMBPS || venue.DL_SPeed_MBPS || 0);
      if (!hasWifi || speed < 100) return false;
    }

    if (filters.hasWifiSilver) {
      const hasWifi = venue.PubWifi === true || venue.PubWifi === 1 || venue.Pub_Wifi === true || venue.Pub_Wifi === 1;
      const speed = parseFloat(venue.DLSPeedMBPS || venue.DL_SPeed_MBPS || 0);
      if (!hasWifi || speed < 25 || speed >= 100) return false;
    }

    if (filters.hasWifiBronze) {
      const hasWifi = venue.PubWifi === true || venue.PubWifi === 1 || venue.Pub_Wifi === true || venue.Pub_Wifi === 1;
      const speed = parseFloat(venue.DLSPeedMBPS || venue.DL_SPeed_MBPS || 0);
      if (!hasWifi || speed < 5 || speed >= 25) return false;
    }

    if (filters.hasWifiWM) {
      if (!wmWifiSlugs.has(venue.slug)) return false;
    }

    if (filters.hasCharging && (!venue.Charging_Ports || venue.Charging_Ports === 0)) return false;

    // Noise filters
    if (filters.isQuiet && venue.Noise_Level !== 'Quiet') return false;
    if (filters.isModerate && venue.Noise_Level !== 'Moderate') return false;
    if (filters.isLively && venue.Noise_Level !== 'Lively') return false;

    // ✅ Open late filter
    if (filters.openLate && venue.Open_Late !== 1) return false;

    // ✅ Veg only filter
    if (filters.vegOnly && venue.Veg_only !== 1) return false;

    // ✅ Outdoor seating filter
    if (filters.outdoorSeating && venue.Outdoor_seating !== 1) return false;

    // Other basic filters
    if (filters.venueType && venue.venue_category_display !== filters.venueType) return false;
    if (filters.minRating && (!venue.Rating || venue.Rating < filters.minRating)) return false;
    if (filters.priceLevel && venue.HL_Price_Level !== parseInt(filters.priceLevel)) return false;
    if (filters.highRated && (!venue.Rating || venue.Rating < 4.0)) return false;
    if (filters.petFriendly && venue.petFriendly !== 1) return false;
    if (filters.Nearby && !venue.isNearby) return false;
    if (filters.Offers && venue.hasOffers !== 1) return false;
    
    // ✅ Rating filters
    if (filters.rating3 && (!venue.Rating || venue.Rating < 3.0)) return false;
    if (filters.rating35 && (!venue.Rating || venue.Rating < 3.5)) return false;
    if (filters.rating4 && (!venue.Rating || venue.Rating < 4.0)) return false;
    if (filters.rating45 && (!venue.Rating || venue.Rating < 4.5)) return false;
    if (filters.rating5 && (!venue.Rating || venue.Rating < 5.0)) return false;

    // ✅ AC/Fan filters
    if (filters.acFanAC && !venue.Has_AC && venue.HL_zoho_AC_Fan !== 'AC') return false;
    if (filters.acFanFan && !venue.Has_Fan && venue.HL_zoho_AC_Fan !== 'Fan') return false;
    
    // ✅ Entry Fees filters
    if (filters.entryFeesYes) {
      const entryFee = venue.Entrance_Fee;
      const hasEntryFee = (typeof entryFee === 'string' && !isNaN(entryFee) && parseFloat(entryFee) > 0) || 
                         (typeof entryFee === 'number' && entryFee > 0);
      if (!hasEntryFee) return false;
    }
    if (filters.entryFeesNo) {
      const entryFee = venue.Entrance_Fee;
      if (!(entryFee === null || entryFee === undefined || entryFee === '' || entryFee === 0 || entryFee === 'None')) return false;
    }
    
    // ✅ Meal time filters
    if (filters.breakfastYes && venue.Breakfast_offered !== 1) return false;
    if (filters.breakfastNo && venue.Breakfast_offered !== 0) return false;
    if (filters.brunchYes && venue.Brunch_offered !== 1) return false;
    if (filters.brunchNo && venue.Brunch_offered !== 0) return false;
    if (filters.dinnerYes && venue.Dinner_offered !== 1) return false;
    if (filters.dinnerNo && venue.Dinner_offered !== 0) return false;
    
    // ✅ Open Late filters
    if (filters.openLateYes && venue.Open_Late !== 1) return false;
    if (filters.openLateNo && venue.Open_Late !== 0) return false;
    
    // ✅ Take Booking filters
    if (filters.takeBookingYes && venue.Takes_bookings !== 1) return false;
    if (filters.takeBookingNo && venue.Takes_bookings !== 0) return false;
    
    // ✅ Parking filters
    if (filters.parkingMall && venue.parking_options !== 'Mall Parking') return false;
    if (filters.parkingValet && venue.parking_options !== 'Valet') return false;
    if (filters.parkingStreet && venue.parking_options !== 'Street') return false;
    if (filters.parkingBasement && venue.parking_options !== 'Basement') return false;
    if (filters.parkingLot && venue.parking_options !== 'Lot') return false;
    if (filters.parkingNone && (venue.parking_options && venue.parking_options !== 'None' && venue.parking_options !== '')) return false;
    
    // ✅ Family Friendly filters
    if (filters.familyScore1 && venue.Family_frienliness_score !== 1) return false;
    if (filters.familyScore2 && venue.Family_frienliness_score !== 2) return false;
    if (filters.familyScore3 && venue.Family_frienliness_score !== 3) return false;
    if (filters.familyScore4 && venue.Family_frienliness_score !== 4) return false;
    if (filters.familyScore5 && venue.Family_frienliness_score !== 5) return false;
    
    // View filters
    if (filters.viewStreet && (!venue.View || !venue.View.toString().toLowerCase().includes('street'))) return false;
    if (filters.viewWaterfront && (!venue.View || !venue.View.toString().toLowerCase().includes('waterfront'))) return false;
    if (filters.viewPark && (!venue.View || !venue.View.toString().toLowerCase().includes('park'))) return false;
    if (filters.viewAinView && (!venue.View || !venue.View.toString().toLowerCase().includes('ain view'))) return false;
    if (filters.viewMarinaSkyline && (!venue.View || !venue.View.toString().toLowerCase().includes('marina skyline'))) return false;
    if (filters.viewSunset && (!venue.View || !venue.View.toString().toLowerCase().includes('sunset'))) return false;
    if (filters.viewBeachView && (!venue.View || !venue.View.toString().toLowerCase().includes('beach view'))) return false;
    if (filters.viewCourtyard && (!venue.View || !venue.View.toString().toLowerCase().includes('courtyard'))) return false;
    if (filters.viewSouq && (!venue.View || !venue.View.toString().toLowerCase().includes('souq'))) return false;
    if (filters.viewBoardwalk && (!venue.View || !venue.View.toString().toLowerCase().includes('boardwalk'))) return false;
    
    return true;
  });

  // Search filter - only apply if searchQuery exists
  if (searchQuery && searchQuery.trim()) {
    const searchLower = searchQuery.toLowerCase();
    filtered = filtered.filter(venue => 
      venue.name?.toLowerCase().includes(searchLower) ||
      venue.name?.toLowerCase().includes(searchLower) ||
      venue.vendorName?.toLowerCase().includes(searchLower) ||
      venue.city?.toLowerCase().includes(searchLower) ||
      venue.city?.toLowerCase().includes(searchLower) ||
      venue.venue_category_display?.toLowerCase().includes(searchLower) ||
      venue.Cuisine_Tags?.toLowerCase().includes(searchLower) ||
      venue.cuisineTags?.toLowerCase().includes(searchLower)
    );
  }
  // ✅ REMOVED: Events mode filter logic (events handled by EventsMapPage.jsx)

  // ✅ SPECIAL: Add mosque venues only when Mosque filter is active
  if (filters.Mosque) {
    const mosqueVenues = venues.filter(venue => 
      venue.venue_category === 'vc_religion_spiritual' || 
      venue.venue_type === 'vt_mosque' || 
      venue.venue_category_display === 'Religion & Spiritual'
    );
    filtered = [...filtered, ...mosqueVenues];
  }

  const filterEndTime = performance.now();
  const filterDuration = (filterEndTime - filterStartTime).toFixed(2);
  
  // ✅ PERFORMANCE: Log filtering performance (only if it takes >50ms)
  if (filterDuration > 50) {
    console.warn(`⚠️ Slow filtering: ${filterDuration}ms for ${nonMosqueVenues.length} venues`);
  } else {
    console.log(`🎯 Filtered ${filtered.length}/${nonMosqueVenues.length} venues in ${filterDuration}ms`);
  }
  
  return filtered;
}, [
  nonMosqueVenues, 
  venues, 
  filters, 
  searchQuery
]); // ✅ OPTIMIZED: Removed isEventsMode, eventsFilters, eventsData dependencies

  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (key.startsWith('hasWifi') && value === true) return true;
    if (key.startsWith('is') && value === true) return true;
    if (typeof value === 'boolean' && value === true) return true;
    if ((typeof value === 'string' && value !== '') || (typeof value === 'number' && value !== 0)) return true;
    return false;
  }).length + (searchQuery.trim() ? 1 : 0);

  // ✅ NEW: Calculate directional venue counts (for arrows showing venues outside viewport)
  const directionalVenueCounts = useMemo(() => {
    if (!mapRef.current || !isMapLoaded || !filteredVenues.length) {
      return { north: 0, south: 0, east: 0, west: 0, inViewport: 0 };
    }
    
    try {
      const map = mapRef.current.getMap();
      const bounds = map.getBounds();
      const centerLat = (bounds._sw.lat + bounds._ne.lat) / 2;
      const centerLng = (bounds._sw.lng + bounds._ne.lng) / 2;
      
      let north = 0, south = 0, east = 0, west = 0, inViewport = 0;
      
      filteredVenues.forEach(venue => {
        const { lat, lng, isValid } = getVenueCoordinates(venue);
        if (!isValid) return;
        
        // Check if venue is inside viewport (regardless of rendering)
        const isInView = lat >= bounds._sw.lat && 
                         lat <= bounds._ne.lat && 
                         lng >= bounds._sw.lng && 
                         lng <= bounds._ne.lng;
        
        if (isInView) {
          inViewport++;
        } else {
          // Determine primary direction (use the axis with greater offset)
          const latOffset = lat - centerLat;
          const lngOffset = lng - centerLng;
          
          if (Math.abs(latOffset) > Math.abs(lngOffset)) {
            // Primary direction is north or south
            if (lat > bounds._ne.lat) north++;
            else if (lat < bounds._sw.lat) south++;
          } else {
            // Primary direction is east or west
            if (lng > bounds._ne.lng) east++;
            else if (lng < bounds._sw.lng) west++;
          }
        }
      });
      
      return { north, south, east, west, inViewport };
    } catch (error) {
      return { north: 0, south: 0, east: 0, west: 0, inViewport: 0 };
    }
  }, [filteredVenues, Math.floor(viewState.zoom), isMapLoaded]); // ✅ OPTIMIZED: Only recalculate on zoom level changes

// ✅ ENHANCED: Intelligent auto-fit map to filtered venues with screen-size aware padding
  const autoFitMapToVenues = useCallback((venuesToFit) => {
  if (!mapRef.current || !venuesToFit || venuesToFit.length === 0) {
    console.log('❌ Cannot auto-fit: no venues or map ref');
      return;
    }

  const map = mapRef.current.getMap();
  console.log('📍 Auto-fitting to', venuesToFit.length, 'venues');
  
  // ✅ ENHANCED: Calculate intelligent padding based on screen size and UI elements
  const getIntelligentPadding = () => {
    const isMobile = screenSize.isMobile;
    const isTablet = screenSize.isTablet;
    
    // Base padding values
    let topPadding = 130;
    let bottomPadding = Math.max((draggableCardHeight || 380) + 120, 380);
    let leftPadding = 80;
    let rightPadding = 80;
    
    // Adjust for screen sizes
    if (isMobile) {
      topPadding = 100; // Reduced for mobile
      bottomPadding = Math.max((draggableCardHeight || 300) + 80, 300); // Smaller drawer on mobile
      leftPadding = 40; // Less side padding on mobile
      rightPadding = 40;
    } else if (isTablet) {
      topPadding = 120;
      bottomPadding = Math.max((draggableCardHeight || 350) + 100, 350);
      leftPadding = 60;
      rightPadding = 60;
    }
    
    return {
      top: topPadding,
      bottom: bottomPadding,
      left: leftPadding,
      right: rightPadding
    };
  };
  
  // ✅ ENHANCED: Calculate intelligent zoom limits based on venue count and screen size
  const getIntelligentZoomLimits = (venueCount) => {
    const isMobile = screenSize.isMobile;
    const isTablet = screenSize.isTablet;
    
    // ✅ ENHANCED: Check if gym category is selected for higher zoom
    const isGymCategory = filters.venueCategory && 
      (filters.venueCategory.includes('vc_gym') || 
       filters.venueCategory.some(cat => cat.toLowerCase().includes('gym')));
    
    // ✅ ENHANCED: Check if dropdown filter is active for more precise zoom
    const isDropdownFilter = Object.entries(filters).some(([key, value]) => {
      return value === true && (
        key.startsWith('hasWifi') || 
        key.startsWith('priceLevel') || 
        key.startsWith('payment') || 
        key.startsWith('rating') ||
        key === 'isQuiet' || key === 'isModerate' || key === 'isLively'
      );
    });
    
    let maxZoom = 13;
    let minZoom = 10;
    
    if (venueCount === 1) {
      maxZoom = isMobile ? 16 : 17; // Closer zoom for single venue
      minZoom = 12;
    } else if (venueCount <= 3) {
      maxZoom = isMobile ? 15 : 16;
      minZoom = 11;
    } else if (venueCount <= 10) {
      maxZoom = isMobile ? 14 : 15;
      minZoom = 10;
    } else {
      maxZoom = isMobile ? 13 : 14;
      minZoom = 9;
    }
    
    // ✅ ENHANCED: Increase zoom for gym category
    if (isGymCategory) {
      maxZoom += 1; // Increase max zoom by 1 level
      minZoom += 0.5; // Increase min zoom by 0.5 level
      console.log('🏋️ Gym category detected - increased zoom levels:', { maxZoom, minZoom });
    }
    
    // ✅ ENHANCED: Increase zoom for dropdown filter selections
    if (isDropdownFilter) {
      maxZoom += 1.5; // Increase max zoom by 1.5 levels for dropdown filters
      minZoom += 1; // Increase min zoom by 1 level for dropdown filters
      console.log('🎯 Dropdown filter detected - increased zoom levels:', { maxZoom, minZoom });
    }
    
    return { maxZoom, minZoom };
  };
  
      // Single venue - zoom in close with intelligent limits
  if (venuesToFit.length === 1) {
      const venue = venuesToFit[0];
    const coords = getValidCoordinates(venue);
      
    if (coords) {
      const { maxZoom } = getIntelligentZoomLimits(1);
      console.log('🎯 Zooming to single venue with max zoom:', maxZoom);
          map.flyTo({
        center: [coords.lng, coords.lat],
        zoom: maxZoom,
            duration: 1000,
        curve: 1.4,
        easing: (t) => t * (2 - t),
            essential: true,
      });
      
      // ✅ ENHANCED: Delay visible venues update until after zoom animation completes
      setTimeout(() => {
        console.log('🎯 Single venue zoom animation completed - updating visible venues');
        // Trigger a re-render of visible venues after zoom completes
        setViewState(prev => ({ ...prev, zoom: map.getZoom() }));
      }, 1100); // Slightly longer than animation duration
      
      return;
    } else {
      console.warn('⚠️ Single venue has invalid coordinates');
      return;
    }
    }

    // Multiple venues - fit bounds with intelligent padding and zoom
    const bounds = new mapboxgl.LngLatBounds();
  let validCount = 0;
    
    venuesToFit.forEach(venue => {
    const coords = getValidCoordinates(venue);
      
    if (coords && !isNaN(coords.lat) && !isNaN(coords.lng)) {
        try {
        bounds.extend([coords.lng, coords.lat]);
        validCount++;
        } catch (error) {
        console.warn('⚠️ Error extending bounds for venue:', venue.name, error);
      }
    } else {
      console.warn('⚠️ Invalid coordinates for venue:', venue.name, coords);
    }
  });

  // ✅ ENHANCED: Validate bounds and apply intelligent fitting
  if (validCount > 0 && !bounds.isEmpty()) {
    try {
      // Double-check bounds validity
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      
      if (isNaN(sw.lng) || isNaN(sw.lat) || isNaN(ne.lng) || isNaN(ne.lat)) {
        throw new Error('Bounds contain NaN coordinates');
      }
      
      const padding = getIntelligentPadding();
      const { maxZoom, minZoom } = getIntelligentZoomLimits(validCount);
      
      console.log('✅ Fitting bounds to', validCount, 'venues with intelligent padding:', padding, 'zoom limits:', { maxZoom, minZoom });
        map.fitBounds(bounds, {
          padding: padding,
          duration: 1000,
        curve: 1.4,
        easing: (t) => t * (2 - t),
          essential: true,
        maxZoom: maxZoom,
        minZoom: minZoom
        });
        
        // ✅ ENHANCED: Delay visible venues update until after zoom animation completes
        setTimeout(() => {
          console.log('🎯 Zoom animation completed - updating visible venues');
          // Trigger a re-render of visible venues after zoom completes
          setViewState(prev => ({ ...prev, zoom: map.getZoom() }));
        }, 1100); // Slightly longer than animation duration
      } catch (error) {
      console.warn('⚠️ Error fitting bounds, falling back to Dubai center:', error);
      // Fallback: fly to Dubai center with screen-aware zoom
      const fallbackZoom = screenSize.isMobile ? 10.5 : screenSize.isTablet ? 11 : 11.5;
        map.flyTo({
          center: [REGION_CONFIG.mapCenter.longitude, REGION_CONFIG.mapCenter.latitude],
          zoom: fallbackZoom,
          duration: 1000
        });
        
        // ✅ ENHANCED: Delay visible venues update until after fallback zoom completes
        setTimeout(() => {
          console.log('🎯 Fallback zoom animation completed - updating visible venues');
          setViewState(prev => ({ ...prev, zoom: fallbackZoom }));
        }, 1100);
      }
    } else {
    console.warn('⚠️ No valid coordinates found, resetting to Dubai center');
    // Fallback: fly to Dubai center with screen-aware zoom
    const fallbackZoom = screenSize.isMobile ? 10.5 : screenSize.isTablet ? 11 : 11.5;
      map.flyTo({
        center: [REGION_CONFIG.mapCenter.longitude, REGION_CONFIG.mapCenter.latitude],
        zoom: fallbackZoom,
        duration: 1000
      });
      
      // ✅ ENHANCED: Delay visible venues update until after fallback zoom completes
      setTimeout(() => {
        console.log('🎯 Second fallback zoom animation completed - updating visible venues');
        setViewState(prev => ({ ...prev, zoom: fallbackZoom }));
      }, 1100);
    }
}, [draggableCardHeight, screenSize]);


// ✅ ENHANCED: Smart auto-fit that only zooms for significant filter changes
  useEffect(() => {
    if (!isMapLoaded || !mapRef.current) return;

  // Add a small delay to ensure the map is ready and filters are applied
    const timeoutId = setTimeout(() => {
    // ✅ CRITICAL FIX: Filter out venues with invalid coordinates
    const validFilteredVenues = filteredVenues.filter(venue => {
      const coords = getValidCoordinates(venue);
      return coords !== null;
    });
    
    const validNonMosqueVenues = nonMosqueVenues.filter(venue => {
      const coords = getValidCoordinates(venue);
      return coords !== null;
    });
    
    // ✅ PRECISE: Only auto-fit for top filters and categories, not venue types or quick filters
    const shouldAutoFit = () => {
      // Define filters that should trigger auto-zoom (top filters and categories)
      const autoZoomFilters = [
        // Top filters (WiFi, Budget, Rating)
        'hasWifi', 'hasWifiVerified', 'hasWifiUnverified', 'hasWifiGold', 'hasWifiSilver', 'hasWifiBronze',
        'priceLevel1', 'priceLevel2', 'priceLevel3', 'priceLevel4',
        'rating3', 'rating35', 'rating4', 'rating45', 'rating5',
        // Categories (main venue categories)
        'venueCategory'
      ];
      
      // Define filters that should NOT trigger auto-zoom (venue types and quick filters)
      const noAutoZoomFilters = [
        // Venue types and subtypes
        'venueSubType', 'cuisineTags', 'dietaryTags', 'groupPolicyTags', 'healthyFoodLevels',
        // Quick filters
        'openLate', 'vegOnly', 'outdoorSeating', 'hasCharging', 'isQuiet', 'isModerate', 'isLively',
        'paymentCash', 'paymentCard', 'paymentCrypto', 'acFanAC', 'acFanFan', 'entryFeesYes', 'entryFeesNo',
        'breakfastYes', 'breakfastNo', 'brunchYes', 'brunchNo', 'dinnerYes', 'dinnerNo',
        'takeBookingYes', 'takeBookingNo', 'parkingMall', 'parkingValet', 'parkingStreet', 'parkingBasement',
        'parkingLot', 'parkingNone', 'familyScore1', 'familyScore2', 'familyScore3', 'familyScore4', 'familyScore5',
        'viewStreet', 'viewWaterfront', 'viewPark', 'viewAinView', 'viewMarinaSkyline', 'viewSunset',
        'viewBeachView', 'viewCourtyard', 'viewSouq', 'viewBoardwalk'
      ];
      
      // Check if any auto-zoom filters are active
      const hasAutoZoomFilters = autoZoomFilters.some(filterKey => {
        const value = filters[filterKey];
        return value === true || (Array.isArray(value) && value.length > 0);
      });
      
      // Check if only no-auto-zoom filters are active
      const hasOnlyNoAutoZoomFilters = Object.entries(filters).some(([key, value]) => {
        if (autoZoomFilters.includes(key)) return false; // Skip auto-zoom filters
        if (noAutoZoomFilters.includes(key)) {
          return value === true || (Array.isArray(value) && value.length > 0);
        }
        return false; // Skip other filters
      });
      
      // Auto-fit only for top filters and categories
      if (hasAutoZoomFilters) {
        console.log('🎯 Top filter or category detected - auto-fitting map');
        return true;
      }
      
      // Don't auto-fit for venue types and quick filters only
      if (hasOnlyNoAutoZoomFilters && !hasAutoZoomFilters) {
        console.log('📍 Venue type or quick filter detected - showing markers without auto-zoom');
        return false;
      }
      
      // Default: no auto-fit
      return false;
    };
    
    if (validFilteredVenues.length > 0) {
      if (shouldAutoFit()) {
      console.log('🎯 Auto-fitting map to', validFilteredVenues.length, 'filtered venues');
      autoFitMapToVenues(validFilteredVenues);
      } else {
        console.log('📍 Showing', validFilteredVenues.length, 'filtered venues without auto-zoom');
      }
    } else if (validNonMosqueVenues.length > 0) {
      // If no venues match filters, show all valid venues
      console.log('🎯 No venues match filters, showing all valid venues');
      autoFitMapToVenues(validNonMosqueVenues);
    }
  }, 200); // Reduced delay for faster response

    return () => clearTimeout(timeoutId);
}, [filteredVenues, isMapLoaded, autoFitMapToVenues, nonMosqueVenues, filters]);

// ✅ FIXED: Auto-fit map when filters change - watches filters directly
// ✅ SIMPLIFIED: Only auto-fit when user explicitly applies filters, not on initial load
useEffect(() => {
  if (!isMapLoaded || !mapRef.current) return;
  
  // ✅ NEW: Don't auto-fit on initial load - let users see all venues
  if (filteredVenues.length === nonMosqueVenues.length) {
    console.log('📍 Showing all venues - no auto-fit needed');
    return;
  }
  
  // Only auto-fit when filters are actually active and reduce venue count significantly
  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'venueCategory' || key === 'venueSubType' || key === 'cuisineTags' || 
        key === 'dietaryTags' || key === 'groupPolicyTags' || key === 'healthyFoodLevels') {
      return Array.isArray(value) && value.length > 0;
    }
    return value === true;
  });
  
  // Only auto-fit if filters are active AND reduce venues by at least 30%
  if (!hasActiveFilters || filteredVenues.length > nonMosqueVenues.length * 0.7) {
    console.log('⚪ Not enough filtering to auto-fit');
    return;
  }
  
  // Filter out venues with invalid coordinates
  const validVenues = filteredVenues.filter(venue => {
    const coords = getValidCoordinates(venue);
    return coords !== null;
  });
  
  if (validVenues.length === 0) {
    console.log('⚠️ No venues with valid coordinates to fit');
    return;
  }
  
  // Debounce the auto-fit
  const timeoutId = setTimeout(() => {
    if (validVenues.length > 0 && validVenues.length < nonMosqueVenues.length * 0.7) {
      console.log('🎯 Auto-fitting to', validVenues.length, 'significantly filtered venues');
      autoFitMapToVenues(validVenues);
    }
  }, 300);
  
  return () => clearTimeout(timeoutId);
}, [filters, filteredVenues.length, isMapLoaded, nonMosqueVenues.length]);

  // ✅ ENHANCED: Handle category filter with auto-fit
const handleCategoryFilter = useCallback((primaryCategories, subCategories = [], cuisineTags = [], dietaryTags = [], groupPolicyTags = [], healthyFoodLevels = [], openLate = false, vegOnly = false, outdoorSeating = false) => {
  console.log('🎯 CategoryCarousel -> VenueMap:', { 
    primaryCategories, 
    subCategories,
    isStreetVendor: Array.isArray(primaryCategories) ? primaryCategories.includes('Street Vendor') : primaryCategories === 'Street Vendor'
  });
  
  // ✅ CRITICAL: Set flag to keep drawer open
  setShouldKeepDrawerOpen(true);
  
  setFilters(prev => ({
    ...prev,
    venueCategory: Array.isArray(primaryCategories) ? primaryCategories : (primaryCategories ? [primaryCategories] : []),
    venueSubType: Array.isArray(subCategories) ? subCategories : (subCategories ? [subCategories] : []),
    cuisineTags: Array.isArray(cuisineTags) ? cuisineTags : (cuisineTags ? [cuisineTags] : []),
    dietaryTags: Array.isArray(dietaryTags) ? dietaryTags : (dietaryTags ? [dietaryTags] : []),
    groupPolicyTags: Array.isArray(groupPolicyTags) ? groupPolicyTags : (groupPolicyTags ? [groupPolicyTags] : []),
    healthyFoodLevels: Array.isArray(healthyFoodLevels) ? healthyFoodLevels : (healthyFoodLevels ? [healthyFoodLevels] : []),
    openLate: openLate,
    vegOnly: vegOnly,
    outdoorSeating: outdoorSeating
  }));
  
  setSelectedPrimaryCategory(primaryCategories);
  setSelectedSubCategory(subCategories);
}, []);


// ✅ FIXED: Toggle filter with proper state updates
const toggleFilter = useCallback((filterKey, value = null) => {
  // Set flag to prevent drawer from closing during filter operations
  setIsApplyingFilters(true);
  isApplyingFiltersRef.current = true;
  
  setFilters(prev => {
    const newFilters = { ...prev };
    
    // ✅ FIXED: Handle WiFi filters (mutually exclusive)
    if (filterKey.startsWith('hasWifi')) {
      // Clear all WiFi sub-filters
      newFilters.hasWifi = false;
      newFilters.hasWifiVerified = false;
      newFilters.hasWifiUnverified = false;
      newFilters.hasWifiGold = false;
      newFilters.hasWifiSilver = false;
      newFilters.hasWifiBronze = false;
      newFilters.hasWifiSpeed = false;
      newFilters.hasWifiWM = false;

      // Toggle the selected filter
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle noise level filters (mutually exclusive)
    else if (filterKey === 'isQuiet' || filterKey === 'isModerate' || filterKey === 'isLively') {
      newFilters.isQuiet = false;
      newFilters.isModerate = false;
      newFilters.isLively = false;
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle view filters (mutually exclusive)
    else if (filterKey.startsWith('view')) {
      newFilters.viewStreet = false;
      newFilters.viewWaterfront = false;
      newFilters.viewPark = false;
      newFilters.viewAinView = false;
      newFilters.viewMarinaSkyline = false;
      newFilters.viewSunset = false;
      newFilters.viewBeachView = false;
      newFilters.viewCourtyard = false;
      newFilters.viewSouq = false;
      newFilters.viewBoardwalk = false;
      
      // Toggle the selected filter
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle price level filters (mutually exclusive)
    else if (filterKey.startsWith('priceLevel')) {
      newFilters.priceLevel1 = false;
      newFilters.priceLevel2 = false;
      newFilters.priceLevel3 = false;
      newFilters.priceLevel4 = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle payment filters (mutually exclusive)
    else if (filterKey.startsWith('payment')) {
      newFilters.paymentCash = false;
      newFilters.paymentCard = false;
      newFilters.paymentCrypto = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle rating filters (mutually exclusive)
    else if (filterKey.startsWith('rating')) {
      newFilters.rating3 = false;
      newFilters.rating35 = false;
      newFilters.rating4 = false;
      newFilters.rating45 = false;
      newFilters.rating5 = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle AC/Fan filters (mutually exclusive)
    else if (filterKey.startsWith('acFan')) {
      newFilters.acFanAC = false;
      newFilters.acFanFan = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle Entry Fees filters (mutually exclusive)
    else if (filterKey.startsWith('entryFees')) {
      newFilters.entryFeesYes = false;
      newFilters.entryFeesNo = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle Breakfast filters (mutually exclusive)
    else if (filterKey.startsWith('breakfast')) {
      newFilters.breakfastYes = false;
      newFilters.breakfastNo = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle Brunch filters (mutually exclusive)
    else if (filterKey.startsWith('brunch')) {
      newFilters.brunchYes = false;
      newFilters.brunchNo = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle Dinner filters (mutually exclusive)
    else if (filterKey.startsWith('dinner')) {
      newFilters.dinnerYes = false;
      newFilters.dinnerNo = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle Open Late filters (mutually exclusive)
    else if (filterKey.startsWith('openLate')) {
      newFilters.openLateYes = false;
      newFilters.openLateNo = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle Take Booking filters (mutually exclusive)
    else if (filterKey.startsWith('takeBooking')) {
      newFilters.takeBookingYes = false;
      newFilters.takeBookingNo = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle Parking filters (mutually exclusive)
    else if (filterKey.startsWith('parking')) {
      newFilters.parkingMall = false;
      newFilters.parkingValet = false;
      newFilters.parkingStreet = false;
      newFilters.parkingBasement = false;
      newFilters.parkingLot = false;
      newFilters.parkingNone = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // Handle Family Friendly filters (mutually exclusive)
    else if (filterKey.startsWith('familyScore')) {
      newFilters.familyScore1 = false;
      newFilters.familyScore2 = false;
      newFilters.familyScore3 = false;
      newFilters.familyScore4 = false;
      newFilters.familyScore5 = false;
      
      newFilters[filterKey] = !prev[filterKey];
    }
    // All other filters
    else {
      newFilters[filterKey] = value !== null ? value : !prev[filterKey];
    }
    
    return newFilters;
  });
  
  // ✅ FIXED: Auto-fit map after WiFi filter change
  setTimeout(() => {
    if (mapRef.current && isMapLoaded && filterKey.startsWith('hasWifi')) {
      console.log('🛜 WiFi filter applied - auto-fitting map');
      autoFitMapToVenues(filteredVenues);
    }
    setIsApplyingFilters(false);
    isApplyingFiltersRef.current = false;
  }, 150);
}, [filteredVenues, autoFitMapToVenues, isMapLoaded]);


  // ✅ ENHANCED: Clear all filters with auto-fit
  const clearAllFilters = useCallback(() => {
    // ✅ FIXED: Set flag to prevent drawer from closing during filter operations
    setIsApplyingFilters(true);
    isApplyingFiltersRef.current = true;
    
    setFilters({
      hasWifi: false,
      hasWifiVerified: false,
      hasWifiUnverified: false,
      hasWifiGold: false,
      hasWifiSilver: false,
      hasWifiBronze: false,
      hasCharging: false,
      isQuiet: false,
      isModerate: false,
      isLively: false,
      hasWifiSpeed: false,
      hasWifiWM: false,
      venueType: '',
      venueCategory: [],
      venueSubType: [],
      cuisineTags: [], // ✅ ADD: Cuisine tags filter
      dietaryTags: [], // ✅ ADD: Dietary tags filter
      groupPolicyTags: [], // ✅ ADD: Group policy tags filter
      healthyFoodLevels: [], // ✅ ADD: Healthy food levels filter
      openLate: false, // ✅ ADD: Open late filter
      vegOnly: false, // ✅ ADD: Veg only filter
      outdoorSeating: false, // ✅ ADD: Outdoor seating filter
      minRating: 0,
      priceLevel: '',
      Mosque: false,
      highRated: false,
      petFriendly: false,
      Nearby: false,
      Offers: false,
      viewStreet: false,
      viewWaterfront: false,
      viewPark: false,
      viewAinView: false,
      viewMarinaSkyline: false,
      viewSunset: false,
      viewBeachView: false,
      viewCourtyard: false,
      viewSouq: false,
      viewBoardwalk: false
    });
    setSearchQuery('');
    setSelectedPrimaryCategory(null);
    setSelectedSubCategory(null);
    
    // ✅ NEW: Trigger map auto-fit after clearing all filters
    setTimeout(() => {
      if (mapRef.current && isMapLoaded) {
        // Show all venues when filters are cleared
        autoFitMapToVenues(nonMosqueVenues);
      }
    }, 100);
    
    // ✅ FIXED: Reset filter operation flag after a short delay
    setTimeout(() => {
      setIsApplyingFilters(false);
      isApplyingFiltersRef.current = false;
    }, 200);
  }, [nonMosqueVenues, isMapLoaded, autoFitMapToVenues]);

  // Screen size handler
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        isMobile: window.innerWidth < 768,
        isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
        isDesktop: window.innerWidth >= 1024
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
  if (visibleVenues && visibleVenues.length > 0) {
    console.log(`🗺️ Attempting to render ${visibleVenues.length} markers`);
    console.log('📍 First venue sample:', {
      name: visibleVenues[0]?.AccountName,
      _id: visibleVenues[0]?._id,
      name: visibleVenues[0]?.name,
      lat: visibleVenues[0]?.lat,
      lng: visibleVenues[0]?.lng
    });
  } else {
    console.log('⚠️ visibleVenues is empty or undefined');
  }
}, [visibleVenues]);

  // ✅ ENHANCED: Intelligent visible venues calculation that prevents marker disappearance
  useEffect(() => {
    if (requestIdRef.current) {
      cancelAnimationFrame(requestIdRef.current);
    }

    const updateVisibleVenues = () => {
      if (mapRef.current && filteredVenues && isMapLoaded && !isZooming) {
        try {
          const map = mapRef.current.getMap();
          const bounds = map.getBounds();
          const currentZoom = map.getZoom();
          
          // ✅ ENHANCED: More generous bounds expansion for better marker visibility
          const getExpandedBounds = (bounds, zoom) => {
            const latDiff = bounds.getNorth() - bounds.getSouth();
            const lngDiff = bounds.getEast() - bounds.getWest();
            
            // More generous expansion to show more markers as user zooms in
            let expansionFactor = 1.0;
            if (zoom >= 17) {
              expansionFactor = 3.5; // Very large expansion for extreme zoom
            } else if (zoom >= 15) {
              expansionFactor = 3.0; // Large expansion for very close zoom
            } else if (zoom >= 13) {
              expansionFactor = 2.5; // Medium expansion for close zoom
            } else if (zoom >= 11.5) {
              expansionFactor = 2.0; // Small expansion for medium zoom
            } else {
              expansionFactor = 1.5; // Minimal expansion for zoomed out
            }
            
            return {
              north: bounds.getNorth() + (latDiff * expansionFactor),
              south: bounds.getSouth() - (latDiff * expansionFactor),
              east: bounds.getEast() + (lngDiff * expansionFactor),
              west: bounds.getWest() - (lngDiff * expansionFactor)
            };
          };
          
          // ✅ ENHANCED: Progressive marker limits - show many more markers
          const getDynamicMaxMarkers = (zoom) => {
            const isMobile = screenSize.isMobile;
            const isTablet = screenSize.isTablet;
            
            if (zoom < 11.5) {
              return isMobile ? 50 : isTablet ? 75 : 100; // ✅ INCREASED: Show more markers when zoomed out
            } else if (zoom < 13) {
              return isMobile ? 70 : isTablet ? 90 : 110;
            } else if (zoom < 15) {
              return isMobile ? 90 : isTablet ? 110 : 130;
            } else if (zoom < 17) {
              return isMobile ? 110 : isTablet ? 130 : 150;
            } else {
              return isMobile ? 150 : isTablet ? 180 : 200; // ✅ INCREASED: Maximum markers
            }
          };
          
        // ✅ ENHANCED: When zoomed out (zoom < 11.5), show more venues regardless of bounds
          if (currentZoom < 11.5) {
            const maxVenuesForZoomedOut = getDynamicMaxMarkers(currentZoom);
            
            // ✅ ENHANCED: Always show more venues when no filters are applied
            const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
              if (key === 'venueCategory' || key === 'venueSubType') return false; // Skip category filters
              return value === true || (Array.isArray(value) && value.length > 0);
            });
            
            // ✅ NEW: If no filters, show up to 100 venues distributed across the map
            if (!hasActiveFilters && !searchQuery.trim()) {
              console.log('📍 No filters - showing up to 100 venues across the map');
              
              // Get venues distributed across the entire map
              const allValidVenues = filteredVenues.filter(venue => {
                const lat = parseFloat(venue.lat);
                const lng = parseFloat(venue.lng);
                return lat && lng && !isNaN(lat) && !isNaN(lng);
              });
              
              // Distribute venues evenly - take every Nth venue to spread across map
              const step = Math.max(1, Math.floor(allValidVenues.length / 100));
              const distributedVenues = allValidVenues.filter((_, index) => index % step === 0).slice(0, 100);
              
              setVisibleVenues(distributedVenues);
              return;
            }
            const centerLat = bounds.getCenter().lat;
            const centerLng = bounds.getCenter().lng;

            // Get gym venues in center area (within 0.1 degrees of center)
            const gymVenuesInCenter = filteredVenues.filter(venue => {
              const lat = parseFloat(venue.lat);
              const lng = parseFloat(venue.lng);
              
              if (!lat || !lng || isNaN(lat) || isNaN(lng)) return false;
              
              // Check if it's a gym venue
              const isGym = venue.venue_category_display === 'GYM' || 
                           venue.venue_category === 'vc_gym' ||
                           (venue.venue_category_display && venue.venue_category_display.toLowerCase().includes('gym'));
              
              // Check if it's in center area (within 0.1 degrees)
              const inCenter = Math.abs(lat - centerLat) < 0.1 && Math.abs(lng - centerLng) < 0.1;
              
              return isGym && inCenter;
            });
            
            // Take up to 5-6 gym venues from center area
            const gymVenuesToShow = gymVenuesInCenter.slice(0, 6);
            
            // Get remaining venues (excluding the gym venues we already selected)
            const remainingVenues = filteredVenues.filter(venue => 
              !gymVenuesToShow.some(gym => (gym._id || gym.Dubai_id) === (venue._id || venue.Dubai_id))
            );
            
            // If no filters are applied, show more venues from the entire dataset
            if (!hasActiveFilters && !searchQuery.trim()) {
              console.log('📍 No filters applied - showing gym venues in center + more venues for better discovery');
              let visible = [...gymVenuesToShow, ...remainingVenues].slice(0, maxVenuesForZoomedOut);
              setVisibleVenues(visible);
              return;
            }
            
            // Combine gym venues with remaining venues for filtered view
            let visible = [...gymVenuesToShow, ...remainingVenues].slice(0, maxVenuesForZoomedOut);
            
            setVisibleVenues(visible);
            return;
          }
          
          // ✅ ENHANCED: Use expanded bounds to prevent marker disappearance
          const expandedBounds = getExpandedBounds(bounds, currentZoom);
          const dynamicMaxMarkers = getDynamicMaxMarkers(currentZoom);
          
          // ✅ ENHANCED: Preserve existing markers during zoom transitions
          const currentVisibleIds = new Set(visibleVenues.map(v => v._id || v.Dubai_id));
          
          let visible = filteredVenues.filter(venue => {
            const lat = parseFloat(venue.lat);
            const lng = parseFloat(venue.lng);
            
            if (!lat || !lng || isNaN(lat) || isNaN(lng)) return false;
            
            // ✅ ENHANCED: Keep existing markers visible during zoom
            const venueId = venue._id || venue.Dubai_id;
            if (currentVisibleIds.has(venueId)) {
              return true; // Keep existing markers visible
            }
            
            // ✅ FIXED: Use expanded bounds to keep markers visible
            return lat >= expandedBounds.south && lat <= expandedBounds.north && 
                   lng >= expandedBounds.west && lng <= expandedBounds.east;
          });

          // ✅ ENHANCED: Sort by existing markers first, then rating and distance
          if (visible.length > dynamicMaxMarkers) {
            const centerLat = bounds.getCenter().lat;
            const centerLng = bounds.getCenter().lng;
            
            visible = visible
              .map(venue => {
                const lat = parseFloat(venue.lat);
                const lng = parseFloat(venue.lng);
                const distance = Math.sqrt(Math.pow(lat - centerLat, 2) + Math.pow(lng - centerLng, 2));
                const venueId = venue._id || venue.Dubai_id;
                const isExisting = currentVisibleIds.has(venueId);
                
                // Check if it's a gym venue in center area
                const isGym = venue.venue_category_display === 'GYM' || 
                             venue.venue_category === 'vc_gym' ||
                             (venue.venue_category_display && venue.venue_category_display.toLowerCase().includes('gym'));
                const inCenter = Math.abs(lat - centerLat) < 0.1 && Math.abs(lng - centerLng) < 0.1;
                const isGymInCenter = isGym && inCenter;
                
                return { ...venue, distance, isExisting, isGymInCenter };
              })
              .sort((a, b) => {
                // ✅ ENHANCED: Prioritize existing markers first, then gym venues in center, then rating, then distance
                if (a.isExisting && !b.isExisting) return -1;
                if (!a.isExisting && b.isExisting) return 1;
                
                // Then prioritize gym venues in center area
                if (a.isGymInCenter && !b.isGymInCenter) return -1;
                if (!a.isGymInCenter && b.isGymInCenter) return 1;
                
                // If both are existing or both are new, sort by rating then distance
                const ratingDiff = (b.Rating || 0) - (a.Rating || 0);
                if (Math.abs(ratingDiff) > 0.1) return ratingDiff;
                return a.distance - b.distance;
              })
              .slice(0, dynamicMaxMarkers)
              .map(({ distance, isExisting, isGymInCenter, ...venue }) => venue); // Remove helper properties
          }
          
          // ✅ ENHANCED: Fallback to ensure markers are always visible
          if (visible.length === 0 && filteredVenues.length > 0) {
            console.log('⚠️ No venues in expanded bounds, falling back to top-rated venues');
            const centerLat = bounds.getCenter().lat;
            const centerLng = bounds.getCenter().lng;
            
            // ✅ ENHANCED: Prioritize gym venues in fallback as well
            const gymVenues = filteredVenues.filter(venue => {
              const isGym = venue.venue_category_display === 'GYM' || 
                           venue.venue_category === 'vc_gym' ||
                           (venue.venue_category_display && venue.venue_category_display.toLowerCase().includes('gym'));
              return isGym;
            });
            
            const nonGymVenues = filteredVenues.filter(venue => {
              const isGym = venue.venue_category_display === 'GYM' || 
                           venue.venue_category === 'vc_gym' ||
                           (venue.venue_category_display && venue.venue_category_display.toLowerCase().includes('gym'));
              return !isGym;
            });
            
            // Process gym venues first
            const processedGymVenues = gymVenues
              .map(venue => {
                const lat = parseFloat(venue.lat);
                const lng = parseFloat(venue.lng);
                if (!lat || !lng || isNaN(lat) || isNaN(lng)) return null;
                const distance = Math.sqrt(Math.pow(lat - centerLat, 2) + Math.pow(lng - centerLng, 2));
                return { ...venue, distance, isGym: true };
              })
              .filter(venue => venue !== null)
              .sort((a, b) => {
                const ratingDiff = (b.Rating || 0) - (a.Rating || 0);
                if (Math.abs(ratingDiff) > 0.1) return ratingDiff;
                return a.distance - b.distance;
              })
              .slice(0, 6); // Show up to 6 gym venues
            
            // Process other venues
            const processedOtherVenues = nonGymVenues
              .map(venue => {
                const lat = parseFloat(venue.lat);
                const lng = parseFloat(venue.lng);
                if (!lat || !lng || isNaN(lat) || isNaN(lng)) return null;
                const distance = Math.sqrt(Math.pow(lat - centerLat, 2) + Math.pow(lng - centerLng, 2));
                return { ...venue, distance, isGym: false };
              })
              .filter(venue => venue !== null)
              .sort((a, b) => {
                const ratingDiff = (b.Rating || 0) - (a.Rating || 0);
                if (Math.abs(ratingDiff) > 0.1) return ratingDiff;
                return a.distance - b.distance;
              });
            
            // Combine gym venues first, then other venues
            visible = [...processedGymVenues, ...processedOtherVenues]
              .slice(0, Math.min(dynamicMaxMarkers, 10)) // Show at least 10 venues as fallback
              .map(({ distance, isGym, ...venue }) => venue);
          }
          
          setVisibleVenues(visible);
        } catch (error) {
          console.warn('Error calculating visible venues:', error);
          setVisibleVenues(filteredVenues.slice(0, MAX_MARKERS));
        }
      }
    };

    // ✅ ENHANCED: Google Maps-like stable marker behavior during zoom
    const isZoomChange = prevZoomRef.current && 
      Math.abs((viewState.zoom || 11) - prevZoomRef.current) > 0.1;
    
    if (isZoomChange) {
      // ✅ OPTIMIZED: Longer debounce for zoom changes to prevent excessive recalculations
      const timeoutId = setTimeout(() => {
      updateVisibleVenues();
      prevZoomRef.current = viewState.zoom;
      }, 500); // ✅ INCREASED: 500ms for more stable zoom behavior (was 300ms)
      
      return () => clearTimeout(timeoutId);
    } else {
      // ✅ CRITICAL: Use RAF for smooth panning instead of setTimeout
      requestIdRef.current = requestAnimationFrame(() => {
        updateVisibleVenues();
        prevZoomRef.current = viewState.zoom;
      });
    }

    return () => {
      if (requestIdRef.current) {
        cancelAnimationFrame(requestIdRef.current);
      }
    };
  }, [Math.floor(viewState.zoom * 10) / 10, filteredVenues, isMapLoaded, screenSize, isZooming]); // ✅ OPTIMIZED: Round zoom to 1 decimal place

  // ✅ ENHANCED: Add drawer interaction state
  const [drawerHeight, setDrawerHeight] = useState(280); 
  const [isDrawerInteracting, setIsDrawerInteracting] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false); // ✅ ADD: Track filter operations
  const drawerTimeoutRef = useRef(null); // ✅ ADD: Missing ref declaration
  const isApplyingFiltersRef = useRef(false); // ✅ ADD: Ref to track filter operations

  // ✅ ENHANCED: Handle map interaction - close drawer more aggressively
  const handleMapInteraction = useCallback((event = null) => {
    // ✅ FIXED: Close drawer on map clicks, but not during filter operations
    if (!isDrawerInteracting && !isApplyingFilters && !isApplyingFiltersRef.current && drawerHeight > 280) {
      // If no event provided, don't close drawer (safer approach)
      if (!event || !event.target) {
        return;
      }
      
      // ✅ FIXED: Check if target is a valid DOM element with closest method
      const target = event.target;
      if (!target || typeof target.closest !== 'function') {
        return;
      }
      
      // ✅ ENHANCED: More comprehensive drawer UI detection
      const isDrawerUI = target.closest('[data-drawer-content]') ||
                        target.closest('.filter-carousel') ||
                        target.closest('[data-filter]') ||
                        target.closest('.category-carousel') ||
                        target.closest('.zones-carousel') ||
                        target.closest('.draggable-card') ||
                        target.closest('button') ||
                        target.closest('select') ||
                        target.closest('input') ||
                        target.closest('.mapboxgl-popup') ||
                        target.closest('.mapboxgl-marker') ||
                        target.closest('[role="button"]') ||
                        target.closest('[data-testid*="filter"]') ||
                        target.closest('[data-testid*="category"]') ||
                        target.closest('[data-testid*="carousel"]');
      
      // ✅ FIXED: Only close drawer if NOT interacting with any drawer UI elements
      if (!isDrawerUI) {
        setDrawerHeight(280); // Close drawer to minimum height
      }
    }
  }, [isDrawerInteracting, isApplyingFilters, drawerHeight]);

  // ✅ ENHANCED: Handle drawer interaction - expand drawer and close dropdowns
  const handleDrawerInteraction = useCallback(() => {
    setIsDrawerInteracting(true);
    
    // ✅ ADD: Dispatch event to close all dropdowns
    const event = new CustomEvent('drawerInteractionStart');
    document.dispatchEvent(event);
    
    // Clear existing timeout
    if (drawerTimeoutRef.current) {
      clearTimeout(drawerTimeoutRef.current);
    }
    
    // Expand drawer
    if (drawerHeight < 400) {
      setDrawerHeight(480);
    }
    
    // Reset interaction state after delay
    drawerTimeoutRef.current = setTimeout(() => {
      setIsDrawerInteracting(false);
    }, 2000);
  }, [drawerHeight]);

  // ✅ ADD: Handle drawer drag start
  const handleDrawerDragStart = useCallback(() => {
    // ✅ ADD: Dispatch event to close all dropdowns when dragging starts
    const event = new CustomEvent('drawerDragStart');
    document.dispatchEvent(event);
  }, []);

  // ✅ ENHANCED: Enhanced map click handler with drawer interaction check
  // ✅ Ref for venue selection handler to avoid hoisting issues
  const handleVenueSelectionRef = useRef(null);
  
  const handleMapClick = useCallback((e) => {
    // ✅ CRITICAL: Check if click originated from drawer or its children
    const target = e.originalEvent?.target || e.target;
    
    // ✅ ENHANCED: More comprehensive drawer detection
    const isDrawerClick = target?.closest?.('[data-drawer="true"]') ||
                        target?.closest?.('[data-drawer-content="true"]') ||
                        target?.closest?.('.filter-carousel') ||
                        target?.closest?.('.category-carousel') ||
                        target?.closest?.('.zones-carousel') ||
                        target?.closest?.('.draggable-card') ||
                        target?.closest?.('[data-testid*="filter"]') ||
                        target?.closest?.('[data-testid*="category"]') ||
                        target?.closest?.('[data-testid*="carousel"]');
    
    if (isDrawerClick) {
      // Click originated from drawer, don't handle as map click
      return;
    }

    // ✅ NEW: Handle native venue layer clicks via React-map-gl
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      if (feature.layer?.id === 'venues-layer') {
        const venueId = feature.properties.id;
        console.log('🎯 Venue clicked via React-map-gl:', feature.properties.name, 'ID:', venueId);
        if (handleVenueSelectionRef.current) {
          handleVenueSelectionRef.current(venueId);
        }
        return; // Don't process as regular map click
      }
    }

  // ✅ NEW: Don't close drawer if filters are being applied
  if (shouldKeepDrawerOpenRef.current || isDrawerInteracting) {
    return;
  }

    if (selectedVenue) {
      setSelectedVenue(null);
      setShowMosquePath(false);
    }
    handleMapInteraction(e);
  }, [selectedVenue, handleMapInteraction, isDrawerInteracting]);

  // ✅ ULTRA-SMOOTH: Enhanced map move handler with optimized performance
const handleMapMove = useCallback((evt) => {
  if (!isDrawerInteracting) {
    handleMapInteraction(evt);
  }
  
  const isZoomingNow = prevZoomRef.current && 
    Math.abs(evt.viewState.zoom - prevZoomRef.current) > 0.05; // Reduced threshold
  
  if (isZoomingNow) {
    setIsZooming(true);
    clearTimeout(smoothZoomTimeoutRef.current);
    smoothZoomTimeoutRef.current = setTimeout(() => setIsZooming(false), 300);
  }
  
  // Use direct state update for immediate response
  setViewState({
    ...evt.viewState,
    transitionDuration: 0,
    transitionInterpolator: undefined
  });
}, [handleMapInteraction, isDrawerInteracting]);

  // ✅ NEW: Throttled map move handler for ultra-smooth performance
  const throttledMapMove = useCallback(
    throttle((evt) => {
      handleMapMove(evt);
    }, ANIMATION_THROTTLE),
    [handleMapMove]
  );

  // ✅ ENHANCED: Enhanced venue click handler
const handleVenueClick = useCallback((venueId) => {
  handleMapInteraction(null);
  
  const venue = venues.find(v => (v._id || v.Dubai_id) === venueId);
  if (venue && mapRef.current) {
    const lat = parseFloat(venue.lat);
    const lng = parseFloat(venue.lng);
    
    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      const map = mapRef.current.getMap();
      
      if (smoothZoomTimeoutRef.current) {
        clearTimeout(smoothZoomTimeoutRef.current);
      }

      const getVenueClickZoom = () => {
        const isMobile = screenSize.isMobile;
        return isMobile ? 16 : 17; // Consistent zoom level
      };
      
      // Use easeTo for faster, smoother zoom
      map.easeTo({
        center: [lng, lat],
        zoom: getVenueClickZoom(),
        duration: 400, // Reduced from 1000
        easing: t => t * (2 - t), // Smooth easing
        essential: true
      });

      smoothZoomTimeoutRef.current = setTimeout(() => {
        setViewState(prev => ({
          ...prev,
          longitude: lng,
          latitude: lat,
          zoom: getVenueClickZoom()
        }));
      }, 450);
    }
  }
  
  setTimeout(() => {
    navigate(`/venue/${venueId}`);
  }, 200); // Reduced delay
}, [navigate, venues, Math.floor(viewState.zoom), handleMapInteraction, screenSize]); // ✅ OPTIMIZED: Only recompute on integer zoom changes

  // ✅ ENHANCED: Enhanced venue selection handler
  const handleVenueSelection = useCallback((venueId) => {
    // Close drawer completely when venue is selected
    setDraggableCardHeight(280); // Close to minimum height
    setIsDrawerInteracting(false);
    
    setSelectedVenue(venueId);
    if (venueId) {
      const selectedVenueData = venues.find(v => (v._id || v.Dubai_id) === venueId);
      setShowMosquePath(selectedVenueData && shouldShowMosqueInfo(selectedVenueData));
      
      // ✅ ENHANCED: Auto-center map when venue is selected
      if (selectedVenueData && mapRef.current) {
        const lat = parseFloat(selectedVenueData.lat);
        const lng = parseFloat(selectedVenueData.lng);
        
        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
          const map = mapRef.current.getMap();
          map.flyTo({
            center: [lng, lat],
            zoom: Math.max(viewState.zoom, 16),
            duration: 1000,
            essential: true
          });
        }
      }
    } else {
      setShowMosquePath(false);
    }
  }, [venues, Math.floor(viewState.zoom), mapRef]); // ✅ OPTIMIZED: Only recompute on integer zoom changes
  
  // ✅ Keep ref up to date
  useEffect(() => {
    handleVenueSelectionRef.current = handleVenueSelection;
  }, [handleVenueSelection]);

const handleDraggableCardHeightChange = useCallback((height) => {
  // ✅ CRITICAL: Don't allow ANY drawer changes if user just applied a filter
  if (shouldKeepDrawerOpenRef.current) {
    return;
  }
  
  setDraggableCardHeight(height);
  
  if (height > 300) {
    handleDrawerInteraction();
  }
}, [handleDrawerInteraction]);

  // ✅ ENHANCED: Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (drawerTimeoutRef.current) {
        clearTimeout(drawerTimeoutRef.current);
      }
    };
  }, [drawerTimeoutRef.current]);

  // ✅ NEW: Handler for native layer venue clicks
  const handleNativeVenueClick = useCallback((venueId, coordinates, properties) => {
    console.log('🎯 Native layer venue clicked:', venueId, properties.name);
    // Find the full venue object and select it
    handleVenueSelection(venueId);
  }, [handleVenueSelection]);
  
  // ✅ NEW: Initialize native venue layer for high-performance rendering
  useNativeVenueLayer(
    mapRef,
    USE_NATIVE_LAYER ? filteredVenues : [], // Only use if enabled
    isMapLoaded,
    handleNativeVenueClick,
    wmWifiSlugs
  );

  const getSpeedTierDisplay = useCallback((venue) => {
    if (!venue.DL_SPeed_MBPS) return null;
    
    const speed = parseFloat(venue.DL_SPeed_MBPS);
    if (speed >= 100) return { tier: 'Gold', emoji: '🥇', color: '#f59e0b' };
    if (speed >= 25) return { tier: 'Silver', emoji: '🥈', color: '#6b7280' };
    if (speed >= 5) return { tier: 'Bronze', emoji: '🥉', color: '#92400e' };
    return { tier: 'Basic', emoji: '📶', color: '#6b7280' };
  }, []);

  // ✅ ENHANCED: Improved venue image function (removed - using native icons)
  const getVenueImage = useCallback((venue) => {
    // Not used with native Mapbox layer - images handled by NativeVenueLayer.js
    return null;
  }, []);

  // ✅ NEW: Handle map load event for optimization
  const handleMapLoad = useCallback(() => {
    setIsMapLoaded(true);
    
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      
      // ✅ PERFORMANCE OPTIMIZATIONS
      map.on('dataloading', () => {
        if (map.getLayer('venue-markers')) {
          map.setLayoutProperty('venue-markers', 'visibility', 'none');
        }
      });
      
      map.on('data', () => {
        if (map.getLayer('venue-markers')) {
          map.setLayoutProperty('venue-markers', 'visibility', 'visible');
        }
      });

      // ✅ SMOOTH SCROLLING OPTIMIZATIONS
      map.on('movestart', () => {
        map.getCanvas().style.cursor = 'grabbing';
      });

      map.on('moveend', () => {
        map.getCanvas().style.cursor = 'grab';
      });

      // ✅ NEW: Optimize rendering during interaction
      map.on('dragstart', () => {
        // Disable transitions during drag
        map.setStyle(map.getStyle());
      });

      map.on('dragend', () => {
        // Re-enable transitions after drag
        map.setStyle(map.getStyle());
      });
    }
  }, []);

  // ✅ NEW: Handle discovery banner click
  const handleDiscoverClick = useCallback(() => {
    setShowDiscovery(true);
    setIsLoadingDiscovery(true);
    
    // Simulate loading time
    setTimeout(() => {
      setIsLoadingDiscovery(false);
    }, 1000);
  }, []);

  useEffect(() => {
    const handleOpenDiscovery = () => {
      setShowDiscovery(true);
      setIsLoadingDiscovery(true);
      setTimeout(() => {
        setIsLoadingDiscovery(false);
      }, 1000);
    };
  
    window.addEventListener('openDiscovery', handleOpenDiscovery);
  
    return () => {
      window.removeEventListener('openDiscovery', handleOpenDiscovery);
    };
  }, []);

  // ✅ NEW: Handle All Events from hamburger drawer
  useEffect(() => {
    const handleOpenAllEvents = () => {
      // ✅ Clear all home page filters when opening events page
      clearAllFilters();
      
      setShowAllEvents(true);
      setIsLoadingAllEvents(true);
      setIsEventsMode(true);
      setTimeout(() => {
        setIsLoadingAllEvents(false);
      }, 1000);
    };
  
    window.addEventListener('openAllEvents', handleOpenAllEvents);
  
    return () => {
      window.removeEventListener('openAllEvents', handleOpenAllEvents);
    };
  }, [clearAllFilters]);
  

  // ✅ NEW: Handle discovery close
  const handleDiscoveryClose = useCallback(() => {
    setShowDiscovery(false);
  }, []);

  // ✅ NEW: Handle discovery card click
  const handleDiscoveryCardClick = useCallback((item) => {
    console.log('🎯 Applying filters from discovery card:', item.title, item.filterAction);
    
    // Close discovery page
    setShowDiscovery(false);
    
    // Apply the filters from the discovery card
    if (item.filterAction) {
      setFilters(prev => ({
        ...prev,
        // Reset all filters first
        hasWifi: false,
        hasWifiVerified: false,
        hasWifiUnverified: false,
        hasWifiGold: false,
        hasWifiSilver: false,
        hasWifiBronze: false,
        hasCharging: false,
        isQuiet: false,
        isModerate: false,
        isLively: false,
        venueCategory: [],
        venueSubType: [],
        cuisineTags: [],
        dietaryTags: [],
        groupPolicyTags: [],
        healthyFoodLevels: [],
        openLate: false,
        vegOnly: false,
        outdoorSeating: false,
        petFriendly: false,
        paymentCard: false,
        paymentCrypto: false,
        // Apply new filters from discovery card
        ...item.filterAction
      }));
      
      // Show success message
      console.log('✅ Filters applied successfully');
    }
  }, []);

  // Add this handler after the existing discovery handlers
  const handleAllEventsClick = useCallback(() => {
    // ✅ Clear all home page filters when opening events page
    clearAllFilters();
    
    setShowAllEvents(true);
    setIsLoadingAllEvents(true);
    setIsEventsMode(true); // Enable events mode for map markers
    
    // Simulate loading time
    setTimeout(() => {
      setIsLoadingAllEvents(false);
    }, 1000);
  }, [clearAllFilters]);

  const handleAllEventsClose = useCallback(() => {
    setShowAllEvents(false);
    // ✅ REMOVED: setIsEventsMode and setEventsFilters (events handled by EventsMapPage.jsx)
  }, []);

  // ✅ REMOVED: handleEventsFilterChange (events handled by EventsMapPage.jsx)
  const handleEventsFilterChange = useCallback((filters) => {
    // No-op
  }, []);

  // ✅ REMOVED: Events mode auto-fit effect (handled by EventsMapPage.jsx)

  // ✅ NEW: Handle clicks outside the drawer
  useEffect(() => {
    const handleClickOutside = (event) => {
      // ✅ CRITICAL: Check if click is outside the drawer
      const drawerElement = document.querySelector('[data-drawer="true"]');
      const drawerContentElement = document.querySelector('[data-drawer-content="true"]');
      
      if (drawerElement && drawerElement.contains(event.target)) {
        // Click is inside drawer, don't close it
        return;
      }
      
      if (drawerContentElement && drawerContentElement.contains(event.target)) {
        // Click is inside drawer content, don't close it
        return;
      }
      
      // ✅ ADD: Check if user is currently interacting with drawer
    if (shouldKeepDrawerOpenRef.current || isDrawerInteracting) {
        return;
      }
      
      // Only close if drawer is open (height > minimum)
      if (draggableCardHeight > 300) {
        handleMapInteraction(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [draggableCardHeight, handleMapInteraction, isDrawerInteracting]);

  if (isLoading && venues.length === 0) return <LoadingSpinner />;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#fef3c7' }}>
      <Header />
      
      
      <div style={{ flex: 1, position: 'relative' }}>
        {/* Top Controls */}
        <div style={{
          position: 'absolute',
          top: '16px',
          left: '16px',
          right: '16px',
          zIndex: 100, // ✅ FIXED: Increased from 5 to 100
          display: 'flex',
          gap: '12px',
          alignItems: 'center'
        }}>
          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              flex: 1,
              position: 'relative',
              maxWidth: '400px',
              zIndex: 1
            }}
          >
           <input
              type="text"
              placeholder="Search venues, cities, or types..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchAndZoom(searchQuery);
                }
              }}
              style={{
                width: '100%',
                padding: '12px 16px 12px 44px',
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <MagnifyingGlassIcon 
              style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                height: '20px',
                width: '20px',
                color: '#6b7280'
              }}
            />
          </motion.div>      

          {/* <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: '12px',
              backgroundColor: 'white',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              position: 'relative'
            }}
          >
            <FunnelIcon style={{ height: '20px', width: '20px', color: '#ea580c' }} />
            {activeFiltersCount > 0 && (
              <div style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                border: '2px solid white'
              }}>
                {activeFiltersCount}
              </div>
            )}
          </motion.button> */}
        </div>        

      <motion.div
        animate={{ 
          bottom: `${Math.max(drawerHeight + 20, 20)}px`
        }}
        transition={{ 
          type: "tween",
          duration: 0.2,
          ease: "easeOut"
        }}
        style={{
          position: 'fixed',
          bottom: `${Math.max(drawerHeight + 20, 20)}px`,
          left: '20px',
          zIndex: 25,
          display: 'flex',
          alignItems: 'center',
          gap: screenSize.isMobile ? '12px' : '16px',
          pointerEvents: 'none'
        }}
      >
        {/* Unified Venue Count Display - Bottom Left */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '10px 16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            border: '2px solid #f5933d',
            minWidth: '90px',
            pointerEvents: 'none'
          }}
        >
          <span style={{
            fontSize: screenSize.isMobile ? '16px' : '18px',
            fontWeight: '800',
            color: '#f5933d',
            lineHeight: '1'
          }}>
            {directionalVenueCounts.inViewport || actualVisibleMarkersCount}
          </span>
          <span style={{
            fontSize: screenSize.isMobile ? '12px' : '14px',
            fontWeight: '600',
            color: '#6b7280',
            lineHeight: '1'
          }}>
            /
          </span>
          <span style={{
            fontSize: screenSize.isMobile ? '12px' : '14px',
            fontWeight: '700',
            color: '#111827',
            lineHeight: '1'
          }}>
            {isEventsMode ? totalEventsCount : filteredVenues.length}
          </span>
          <span style={{
            fontSize: screenSize.isMobile ? '12px' : '14px',
            fontWeight: '600',
            color: '#6b7280',
            lineHeight: '1'
          }}>
            /
          </span>
          <span style={{
            fontSize: screenSize.isMobile ? '11px' : '13px',
            fontWeight: '500',
            color: '#9ca3af',
            lineHeight: '1'
          }}>
            {venues.length}
          </span>
        </motion.div>

        {/* WiFi Toggle Button */}
        {!isEventsMode && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              border: showWifiBadges ? '2px solid #f5933d' : '2px solid transparent',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              padding: screenSize.isMobile ? '8px 12px' : '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              pointerEvents: 'auto',
              minWidth: '50px',
              willChange: 'transform'
            }}
            onClick={() => {
              setShowWifiBadges(!showWifiBadges);
              setShowBudgetBadges(false);
              setShowRatingsBadges(false);
              setShowTimeBadges(false);
              setShowPaymentBadges(false);
            }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                fontSize: screenSize.isMobile ? '20px' : '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="WiFi"
            >
              🛜
            </motion.div>
          </motion.div>
        )}

        {/* Budget Toggle Button */}
        {!isEventsMode && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.05 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              border: showBudgetBadges ? '2px solid #f5933d' : '2px solid transparent',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              padding: screenSize.isMobile ? '8px 12px' : '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              pointerEvents: 'auto',
              minWidth: '50px',
              willChange: 'transform'
            }}
            onClick={() => {
              setShowBudgetBadges(!showBudgetBadges);
              setShowWifiBadges(false);
              setShowRatingsBadges(false);
              setShowTimeBadges(false);
              setShowPaymentBadges(false);
            }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                fontSize: screenSize.isMobile ? '20px' : '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Budget"
            >
              $
            </motion.div>
          </motion.div>
        )}

        {/* Rating Toggle Button */}
        {!isEventsMode && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              border: showRatingsBadges ? '2px solid #f5933d' : '2px solid transparent',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              padding: screenSize.isMobile ? '8px 12px' : '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              pointerEvents: 'auto',
              minWidth: '50px',
              willChange: 'transform'
            }}
            onClick={() => {
              setShowRatingsBadges(!showRatingsBadges);
              setShowWifiBadges(false);
              setShowBudgetBadges(false);
              setShowTimeBadges(false);
              setShowPaymentBadges(false);
            }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                fontSize: screenSize.isMobile ? '20px' : '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Rating"
            >
              ⭐
            </motion.div>
          </motion.div>
        )}
      </motion.div>


        {/* Events Page Button - Separate from count - Only show in events mode */}
        {isEventsMode && (
        <motion.div
          animate={{ 
            bottom: `${Math.max(drawerHeight + 20, 20)}px` // ✅ FIXED: Ensure minimum 20px from bottom
          }}
          transition={{ 
            type: "tween", // ✅ CHANGED: Linear transition instead of spring
            duration: 0.2, // ✅ FIXED: Consistent 200ms duration
            ease: "easeOut"
          }}
          style={{
            position: 'fixed',
            bottom: `${Math.max(drawerHeight + 20, 20)}px`, // ✅ FIXED: Dynamic positioning with minimum
            left: screenSize.isMobile ? '80px' : '100px', // Positioned to the right of count with gap
            backgroundColor: 'white',
            borderRadius: '16px',
            border: showTimeBadges ? '2px solid #f5933d' : 'none',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            padding: screenSize.isMobile ? '8px 12px' : '10px 14px',
            zIndex: 25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: 'auto',
            // ✅ ADDED: Safety constraints
            minWidth: '50px',
            maxWidth: '160px',
            // ✅ ADDED: Hardware acceleration for smoother animation
            willChange: 'transform',
            transform: 'translateZ(0)'
          }}
          onClick={() => {
            setShowTimeBadges(!showTimeBadges);
            setShowWifiBadges(false);
            setShowBudgetBadges(false);
            setShowRatingsBadges(false);
            // Allow multi-selection with payment badges
          }}
        >
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{
            fontSize: screenSize.isMobile ? '20px' : '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
          title="Time"
        >
          ⏱️
        </motion.div>
        </motion.div>
        )}


        {/* Payment Button - Next to dollar button - Only show in events mode */}
        {isEventsMode && (
        <motion.div
          animate={{ 
            bottom: `${Math.max(drawerHeight + 20, 20)}px` // ✅ FIXED: Ensure minimum 20px from bottom
          }}
          transition={{ 
            type: "tween", // ✅ CHANGED: Linear transition instead of spring
            duration: 0.2, // ✅ FIXED: Consistent 200ms duration
            ease: "easeOut"
          }}
          style={{
            position: 'fixed',
            bottom: `${Math.max(drawerHeight + 20, 20)}px`, // ✅ FIXED: Dynamic positioning with minimum
            left: screenSize.isMobile ? '140px' : '170px', // Positioned to the right of timer button with gap
            backgroundColor: 'white',
            borderRadius: '16px',
            border: showPaymentBadges ? '2px solid #f5933d' : 'none',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            padding: screenSize.isMobile ? '8px 12px' : '10px 14px',
            zIndex: 25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: 'auto',
            // ✅ ADDED: Safety constraints
            minWidth: '50px',
            maxWidth: '160px',
            // ✅ ADDED: Hardware acceleration for smoother animation
            willChange: 'transform',
            transform: 'translateZ(0)'
          }}
          onClick={() => {
            // Toggle payment badges
            setShowPaymentBadges(!showPaymentBadges);
            setShowWifiBadges(false);
            setShowBudgetBadges(false);
            setShowRatingsBadges(false);
            // Allow multi-selection with time badges
          }}
        >
          <motion.div
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
  style={{
    fontSize: screenSize.isMobile ? '20px' : '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontWeight: '600'
  }}
  title="Payment"
>
  $
          </motion.div>
        </motion.div>
        )}

        {/* ✅ ENHANCED: Distance Display for selected venue */}
        {selectedVenue && selectedVenueNearestMosque && mosqueRoute && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            style={{
              position: 'absolute',
              top: screenSize.isMobile ? '10vh' : '120px',
              right: screenSize.isMobile ? '2vh' : '1vh',
              backgroundColor: '#0ea5e9',
              color: 'white',
              padding: screenSize.isMobile ? '8px 12px' : '10px 16px',
              borderRadius: '20px',
              zIndex: 99,
              fontSize: screenSize.isMobile ? '11px' : '12px',
              fontWeight: '600',
              boxShadow: '0 6px 16px rgba(14, 165, 233, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>🕌</span>
            <span>{mosqueRoute.distance} km • {mosqueRoute.duration} min walk</span>
          </motion.div>
        )}

        {/* Map Style Selector */}   
        <div
          style={{
            position: 'absolute',
            top: screenSize.isMobile ? '75px' : '85px',
            right: screenSize.isMobile ? '10px' : '15px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            zIndex: 10,  // ✅ REDUCED FROM HIGHER VALUE (was likely 1000+)
            pointerEvents: 'auto'  // ✅ ENSURE CLICK EVENTS WORK
          }}
        >
          {/* ✅ SIMPLIFIED: Single satellite toggle button */}
          <motion.button
            onClick={() => setMapStyle(
              mapStyle.includes('satellite') 
                ? 'mapbox://styles/mapbox/streets-v11' 
                : 'mapbox://styles/mapbox/satellite-streets-v11'
            )}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            style={{
              width: screenSize.isMobile ? '40px' : '44px',
              height: screenSize.isMobile ? '40px' : '44px',
              borderRadius: '8px',
              border: mapStyle.includes('satellite')
                ? '2px solid #f5933d'
                : '1px solid rgba(0, 0, 0, 0.1)',
              backgroundColor: mapStyle.includes('satellite')
                ? 'rgba(245, 147, 61, 0.1)'
                : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              fontSize: screenSize.isMobile ? '18px' : '20px',
              transition: 'all 0.2s ease',
              zIndex: 10,
              position: 'relative'
            }}
            title={mapStyle.includes('satellite') ? 'Street View' : 'Satellite View'}
          >
            {mapStyle.includes('satellite') ? '🗺️' : '🛰️'}
          </motion.button>
        </div>

        {/* ✅ ULTRA-OPTIMIZED MAP with ultra-smooth settings */}
          <MapGL
            ref={mapRef}
            {...viewState}
            {...ULTRA_SMOOTH_MAP_SETTINGS}
            onMove={handleMapMove} // Use direct handler, not throttled
            onLoad={handleMapLoad}
            onClick={handleMapClick}
            onMouseDown={handleMapInteraction}
            onTouchStart={handleMapInteraction}
            onDrag={handleMapInteraction}
            onZoom={handleMapInteraction}
            onZoomEnd={() => setIsZooming(false)} // Add zoom end handler
            style={{ 
              width: '100%', 
              height: '100%',
              willChange: 'transform',
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden'
            }}
            mapStyle={mapStyle}
            mapboxAccessToken={MAPBOX_TOKEN}
            attributionControl={false}
            interactiveLayerIds={USE_NATIVE_LAYER ? ['venues-layer'] : []}
            clickTolerance={3}
            // Enhanced settings for smooth zoom
            scrollZoom={{ 
              speed: 1.2,
              smooth: true
            }}
            touchZoomRotate={{ 
              momentum: true,
              deceleration: 0.99
            }}
            maxTileCacheSize={100}
            refreshExpiredTiles={false}
            fadeDuration={0}
            maxZoom={ZOOM_LEVELS.MAXIMUM_ZOOM}
            minZoom={ZOOM_LEVELS.WORLD_VIEW}                      
            doubleClickZoom={true}
            boxZoom={true}
            keyboard={true}
          >
          <GeolocateControl
            position="top-right"
            style={{ 
              marginTop: screenSize.isMobile ? '50vh' : 
                        screenSize.isTablet ? '50vh' : 
                        '400px', 
              marginRight: screenSize.isMobile ? '20px' : '22px' 
            }}
            trackUserLocation={true}
            showUserHeading={true}
          />

          {/* ✅ ADD REAL-TIME VENDOR INDICATOR */}
          {realtimeVendorLocations.size > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{
                position: 'fixed',
                top: '80px',
                right: '16px',
                backgroundColor: '#10b981',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                zIndex: 100,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: 'white',
                  borderRadius: '50%'
                }}
              />
              <span>🚗 {realtimeVendorLocations.size} vendor{realtimeVendorLocations.size !== 1 ? 's' : ''} live</span>
            </motion.div>
          )}

{/* ✅ React markers removed - using native Mapbox layer for better performance (USE_NATIVE_LAYER = true) */}


          {/* ✅ SELECTIVE: Show only nearest mosque marker for selected venue */}
          <AnimatePresence>
            {selectedVenue && selectedVenueNearestMosque && venues.map((mosque, index) => {
              if (mosque.venue_category !== 'vc_religion_spiritual' && 
                  mosque.venue_type !== 'vt_mosque' && 
                  mosque.venue_category_display !== 'Religion & Spiritual') return null;
              
              const isThisTheNearest = (mosque._id || mosque.Dubai_id) === (selectedVenueNearestMosque._id || selectedVenueNearestMosque.Dubai_id);
              if (!isThisTheNearest) return null;
              
              return (
                <NearestMosqueMarker
                  key={`nearest-mosque-${mosque._id || mosque.Dubai_id || index}`}
                  mosque={mosque}
                  selectedVenue={selectedVenue}
                  allVenues={venues}
                  showPath={showMosquePath}
                />
              );
            })}
          </AnimatePresence>
        </MapGL>
          
          {/* Directional Arrows - Show venues outside viewport */}
          {(directionalVenueCounts.north > 0 || directionalVenueCounts.south > 0 || 
            directionalVenueCounts.east > 0 || directionalVenueCounts.west > 0) && (
            <>
              {/* North Arrow */}
              {directionalVenueCounts.north > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    position: 'absolute',
                    top: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(245, 147, 61, 0.95)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '20px',
                    padding: '6px 14px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    pointerEvents: 'auto'
                  }}
                  onClick={() => {
                    const map = mapRef.current?.getMap();
                    if (map) map.panBy([0, -200], { duration: 300 });
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span style={{ color: 'white', fontSize: '14px' }}>↑</span>
                  <span style={{ color: 'white', fontSize: '12px', fontWeight: '600' }}>
                    {directionalVenueCounts.north}
                  </span>
                </motion.div>
              )}
              
              {/* South Arrow */}
              {directionalVenueCounts.south > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    position: 'absolute',
                    bottom: `${Math.max(drawerHeight + 80, 100)}px`,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(245, 147, 61, 0.95)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '20px',
                    padding: '6px 14px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    pointerEvents: 'auto'
                  }}
                  onClick={() => {
                    const map = mapRef.current?.getMap();
                    if (map) map.panBy([0, 200], { duration: 300 });
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span style={{ color: 'white', fontSize: '14px' }}>↓</span>
                  <span style={{ color: 'white', fontSize: '12px', fontWeight: '600' }}>
                    {directionalVenueCounts.south}
                  </span>
                </motion.div>
              )}
              
              {/* East Arrow */}
              {directionalVenueCounts.east > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '20px',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(245, 147, 61, 0.95)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '20px',
                    padding: '6px 14px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    pointerEvents: 'auto'
                  }}
                  onClick={() => {
                    const map = mapRef.current?.getMap();
                    if (map) map.panBy([200, 0], { duration: 300 });
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span style={{ color: 'white', fontSize: '12px', fontWeight: '600' }}>
                    {directionalVenueCounts.east}
                  </span>
                  <span style={{ color: 'white', fontSize: '14px' }}>→</span>
                </motion.div>
              )}
              
              {/* West Arrow */}
              {directionalVenueCounts.west > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '20px',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(245, 147, 61, 0.95)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '20px',
                    padding: '6px 14px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    pointerEvents: 'auto'
                  }}
                  onClick={() => {
                    const map = mapRef.current?.getMap();
                    if (map) map.panBy([-200, 0], { duration: 300 });
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span style={{ color: 'white', fontSize: '14px' }}>←</span>
                  <span style={{ color: 'white', fontSize: '12px', fontWeight: '600' }}>
                    {directionalVenueCounts.west}
                  </span>
                </motion.div>
              )}
            </>
          )}

        {/* Draggable Bottom Card */}
        <DraggableCard 
          initialHeight={380}
          maxHeight={550}
          minHeight={280}
          externalHeight={drawerHeight}
          onHeightChange={setDrawerHeight}
          onInteractionStart={handleDrawerDragStart} // ✅ ADD: Pass drag start handler
          onDrawerStateChange={(state) => { /* console.log('Drawer state:', state)} */ }} 
        >
  {/* ✅ FIXED: Remove padding wrapper when showing DiscoveryPage */}
  {showDiscovery ? (
    <DiscoveryPage 
      onCardClick={handleDiscoveryCardClick}
      onClose={handleDiscoveryClose}
    />
  ) : showAllEvents ? (
    <AllEventsPage 
      onClose={handleAllEventsClose}
      onFilterChange={handleEventsFilterChange}
    />
  ) : (
          <div style={{ padding: '16px' }}>
      {/* ✅ Show loader when loading discovery */}
      {isLoadingDiscovery ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '300px',
          gap: '20px'
        }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #ea580c',
              borderRadius: '50%'
            }}
          />
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#374151',
            textAlign: 'center'
          }}>
            Loading Discovery...
          </div>
          <div style={{
            fontSize: '14px',
            color: '#6b7280',
            textAlign: 'center'
          }}>
            Preparing amazing venues for you
          </div>
        </div>
      ) : (
        <>
            <FilterCarousel
              filters={filters}
              onToggleFilter={toggleFilter}
              onClearAllFilters={clearAllFilters}
              venues={venues}
              wmWifiSlugs={wmWifiSlugs}
            />
            
            <CategoryCarousel 
              onCategorySelect={handleCategoryFilter}
              selectedPrimaryCategory={selectedPrimaryCategory}
              selectedSubCategory={selectedSubCategory}
            />

            <section>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#111827',
                  margin: 0
                }}>
                  {(() => {
                    if (filters.hasWifiGold) return 'Gold WiFi Venues (100+ Mbps)';
                    if (filters.hasWifiSilver) return 'Silver WiFi Venues (25+ Mbps)';
                    if (filters.hasWifiBronze) return 'Bronze WiFi Venues (5+ Mbps)';
                    if (filters.hasWifiVerified) return 'Verified WiFi Venues';
                    if (filters.hasWifiUnverified) return 'Standard WiFi Venues';
                    if (filters.hasWifi) return 'WiFi Available Venues';
                    if (filters.isQuiet) return 'Quiet Venues';
                    if (filters.isModerate) return 'Moderate Noise Venues';
                    if (filters.isLively) return 'Lively Venues';
                    if (filters.Mosque) return 'Mosque Venues';
                    if (filters.venueCategory || filters.venueSubType) {
                      return `${(filters.venueCategory || filters.venueSubType || []).join(', ').trim()} Venues`;
                    }
                    return 'Dubai Venues';
                  })()}
                </h2>
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  padding: '4px 8px',
                  borderRadius: '12px'
                }}>
                  {filteredVenues.length} venues
                </div>
              </div>

              {activeFiltersCount > 0 && (
                <div style={{
                  textAlign: 'center',
                  marginBottom: '16px',
                  padding: '8px 12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#6b7280'
                }}>
                  <span>
                  {filteredVenues.length} venues match your filters • {viewState.zoom < 11.5 ? filteredVenues.length : visibleVenues.length} visible on map
                  </span>
                </div>
              )}

            {/* ✅ FIXED: Venue Cards with complete implementation */}
              <div 
                className="venue-cards-container"
                style={{
                  display: 'flex',
                  gap: '16px',
                  overflowX: 'auto',
                  paddingBottom: '8px',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                {filteredVenues.length > 0 ? (
                  filteredVenues.slice(0, 20).map((venue, index) => {
                    const venueId = venue._id || venue.Dubai_id || `venue-${index}`;
                    const speedTier = getSpeedTierDisplay(venue);
                    const hasVerifiedBadge = venue.Wifi_bage === 'Verified';
                    const isMosque = venue.venue_type === 'vt_mosque';

                    return (
                      <motion.div
                        key={venueId}
                        onClick={() => {
                          // ✅ FIXED: Only call handleVenueClick for direct navigation
                          // Remove handleVenueSelection to prevent preview popup
                          handleVenueClick(venueId);
                        }}
                        whileHover={{ y: -6, scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        style={{
                          minWidth: '260px',
                          maxWidth: '260px',
                          backgroundColor: 'white', // ✅ FIXED: Remove selected state styling
                          borderRadius: '20px',
                          border: '2px solid rgba(0,0,0,0.08)', // ✅ FIXED: Remove selected border
                          cursor: 'pointer',
                          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)', // ✅ FIXED: Remove selected shadow
                          overflow: 'hidden',
                          position: 'relative',
                          transform: 'translateY(0px)' // ✅ FIXED: Remove selected transform
                        }}
                      >
                        {/* Venue card header */}
                        <div style={{
                          height: '140px',
                          background: (() => {
                            if (filters.Mosque && isMosque) return 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)';
                            if (filters.hasWifiGold && speedTier?.tier === 'Gold') return 'linear-gradient(135deg, #f59e0b 0%, #FCD34D 100%)';
                            if (filters.hasWifiSilver && speedTier?.tier === 'Silver') return 'linear-gradient(135deg, #6b7280 0%, #9CA3AF 100%)';
                            if (filters.hasWifiBronze && speedTier?.tier === 'Bronze') return 'linear-gradient(135deg, #92400e 0%, #D97706 100%)';
                            if (selectedVenue === venueId) return 'linear-gradient(135deg, #D9641F 0%, #F4C746 50%, #6BA04A 100%)';
                            return 'linear-gradient(135deg, #184A99 0%, #3B82F6 50%, #6BA04A 100%)';
                          })(),
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden'
                        }}>
                          <motion.div
                            animate={{
                              background: [
                                'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.15) 0%, transparent 50%)',
                                'radial-gradient(circle at 80% 70%, rgba(255,255,255,0.15) 0%, transparent 50%), radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15) 0%, transparent 50%)'
                              ]
                            }}
                            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 1
                            }}
                          />
                          
                          <img
                            src={getVenueImage(venue)}
                            alt={venue.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              zIndex: 2
                            }}
                          />

                          {/* Rating badge and tier badge */}
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            left: '12px',
                            right: '12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            zIndex: 4
                          }}>
                            {venue.Rating && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.2 }}
                                style={{
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                  color: 'white',
                                  fontSize: '12px',
                                  padding: '6px 10px',
                                  borderRadius: '12px',
                                  fontWeight: '700',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  backdropFilter: 'blur(10px)',
                                  border: '1px solid rgba(255,255,255,0.1)'
                                }}
                              >
                                <span style={{ fontSize: '10px' }}>⭐</span>
                                {venue.Rating}
                              </motion.div>
                            )}

                            {isMosque && filters.Mosque ? (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.3 }}
                                style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  color: '#2563eb',
                                  fontSize: '10px',
                                  padding: '4px 8px',
                                  borderRadius: '10px',
                                  fontWeight: '700',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                              >
                                <span style={{ fontSize: '12px' }}>🕌</span>
                                MOSQUE
                              </motion.div>
                            ) : speedTier && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.3 }}
                                style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  color: speedTier.color,
                                  fontSize: '10px',
                                  padding: '4px 8px',
                                  borderRadius: '10px',
                                  fontWeight: '700',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                              >
                                <span style={{ fontSize: '12px' }}>{speedTier.emoji}</span>
                                {speedTier.tier}
                              </motion.div>
                            )}
                          </div>

                          {/* Speed badge */}
                          {venue.DL_SPeed_MBPS && (
                            <motion.div
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ delay: 0.4 }}
                              style={{
                                position: 'absolute',
                                bottom: '12px',
                                left: '12px',
                                backgroundColor: (() => {
                                  const speed = parseFloat(venue.DL_SPeed_MBPS);
                                  if (speed >= 100) return 'rgba(245, 158, 11, 0.95)'; // Gold
                                  if (speed >= 25) return 'rgba(107, 114, 128, 0.95)'; // Silver
                                  if (speed >= 5) return 'rgba(146, 64, 14, 0.95)'; // Bronze
                                  return 'rgba(59, 130, 246, 0.95)'; // Default blue
                                })(),
                                color: 'white',
                                fontSize: '11px',
                                padding: '6px 10px',
                                borderRadius: '10px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                boxShadow: (() => {
                                  const speed = parseFloat(venue.DL_SPeed_MBPS);
                                  if (speed >= 100) return '0 2px 8px rgba(245, 158, 11, 0.3)'; // Gold
                                  if (speed >= 25) return '0 2px 8px rgba(107, 114, 128, 0.3)'; // Silver
                                  if (speed >= 5) return '0 2px 8px rgba(146, 64, 14, 0.3)'; // Bronze
                                  return '0 2px 8px rgba(59, 130, 246, 0.3)'; // Default blue
                                })()
                              }}
                            >
                              <span style={{ fontSize: '1000px' }}>📶</span>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: '1.2' }}>
                                <div style={{ fontSize: '10px', fontWeight: '700' }}>
                                  {venue.DL_SPeed_MBPS} Mbps
                                </div>
                                <div style={{ fontSize: '8px', opacity: 0.9 }}>
                                  {venue.UL_SPeed_MBPS ? `${venue.UL_SPeed_MBPS} up` : 'Speed test'}
                                </div>
                              </div>
                            </motion.div>
                          )}

                          {/* Budget badge - Budget Toggle */}
                          {showBudgetBadges && venue.Budget_Friendly && (
                            <motion.div
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ delay: 0.4 }}
                              style={{
                                position: 'absolute',
                                bottom: '12px',
                                right: '12px',
                                backgroundColor: (() => {
                                  const budgetString = venue.Budget_Friendly;
                                  if (budgetString === '$$$$') return 'rgba(239, 68, 68, 0.95)'; // Very Expensive - Red
                                  if (budgetString === '$$$') return 'rgba(251, 146, 60, 0.95)'; // Expensive - Orange
                                  if (budgetString === '$$') return 'rgba(34, 197, 94, 0.95)'; // Moderate - Green
                                  if (budgetString === '$') return 'rgba(59, 130, 246, 0.95)'; // Budget - Blue
                                  return 'rgba(107, 114, 128, 0.95)'; // Default gray
                                })(),
                                color: 'white',
                                fontSize: '11px',
                                padding: '6px 10px',
                                borderRadius: '10px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                boxShadow: (() => {
                                  const budgetString = venue.Budget_Friendly;
                                  if (budgetString === '$$$$') return '0 2px 8px rgba(239, 68, 68, 0.3)';
                                  if (budgetString === '$$$') return '0 2px 8px rgba(251, 146, 60, 0.3)';
                                  if (budgetString === '$$') return '0 2px 8px rgba(34, 197, 94, 0.3)';
                                  if (budgetString === '$') return '0 2px 8px rgba(59, 130, 246, 0.3)';
                                  return '0 2px 8px rgba(107, 114, 128, 0.3)';
                                })()
                              }}
                            >
                              <span style={{ fontSize: '12px' }}>💰</span>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: '1.2' }}>
                                <div style={{ fontSize: '10px', fontWeight: '700' }}>
                                  {venue.Budget_Friendly}
                                </div>
                                <div style={{ fontSize: '8px', opacity: 0.9 }}>
                                  {(() => {
                                    const budgetString = venue.Budget_Friendly;
                                    if (budgetString === '$$$$') return 'Very Expensive';
                                    if (budgetString === '$$$') return 'Expensive';
                                    if (budgetString === '$$') return 'Moderate';
                                    if (budgetString === '$') return 'Budget';
                                    return 'Price';
                                  })()}
                                </div>
                              </div>
                            </motion.div>
                          )}

                          {/* Verified badge */}
                          {!speedTier && hasVerifiedBadge && !isMosque && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.3 }}
                              style={{
                                position: 'absolute',
                                top: '12px',
                                right: '12px',
                                backgroundColor: '#10B981',
                                color: 'white',
                                fontSize: '10px',
                                padding: '4px 8px',
                                borderRadius: '10px',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                                border: '1px solid rgba(255,255,255,0.2)'
                              }}
                            >
                              Verified
                            </motion.div>
                          )}
                        </div>

                        {/* Venue details */}
                        <div style={{
                          padding: '20px',
                          background: selectedVenue === venueId
                            ? 'linear-gradient(135deg, rgba(217, 100, 31, 0.02) 0%, rgba(24, 74, 153, 0.02) 100%)'
                            : 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(248,250,252,0.8) 100%)'
                        }}>
                          <motion.h4 style={{
                            fontSize: screenSize.isMobile ? '20px' : '17px', // ✅ INCREASED: From 17px to 20px on mobile
                            fontWeight: '800',
                            color: selectedVenue === venueId ? '#184A99' : '#111827',
                            margin: '0 0 10px 0',
                            lineHeight: '1.3',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            letterSpacing: '-0.02em'
                          }}>
                            {venue.name}
                          </motion.h4>

                          <p style={{
                            fontSize: screenSize.isMobile ? '16px' : '13px', // ✅ INCREASED: From 13px to 16px on mobile
                            color: selectedVenue === venueId ? '#6BA04A' : '#64748B',
                            margin: '0 0 12px 0',
                            fontWeight: '600',
                            textTransform: 'capitalize'
                          }}>
                            {venue.venue_category_display || venue.venue_type_display}
                          </p>

                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: screenSize.isMobile ? '14px' : '12px', // ✅ INCREASED: From 12px to 14px on mobile
                            color: '#6B7280',
                            fontWeight: '500',
                            marginBottom: '12px'
                          }}>
                            <span style={{ fontSize: screenSize.isMobile ? '14px' : '12px' }}>📍</span>
                            <span style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {venue.district || venue.city}
                            </span>
                          </div>

                          {/* Feature badges */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
                            gap: '6px',
                            marginTop: '8px'
                          }}>
                            {venue.Pub_Wifi === 1 && (
                              <motion.span
                                whileHover={{ scale: 1.05 }}
                                style={{
                                  padding: screenSize.isMobile ? '6px 10px' : '4px 8px', // ✅ INCREASED: Padding on mobile
                                  backgroundColor: filters.hasWifi || filters.hasWifiVerified || filters.hasWifiUnverified || filters.hasWifiGold || filters.hasWifiSilver || filters.hasWifiBronze ? '#FEF3C7' : '#DBEAFE',
                                  color: filters.hasWifi || filters.hasWifiVerified || filters.hasWifiUnverified || filters.hasWifiGold || filters.hasWifiSilver || filters.hasWifiBronze ? '#F59E0B' : '#1E40AF',
                                  fontSize: screenSize.isMobile ? '11px' : '9px', // ✅ INCREASED: From 9px to 11px on mobile
                                  borderRadius: '10px',
                                  fontWeight: '600',
                                  textAlign: 'center',
                                  border: filters.hasWifi || filters.hasWifiVerified || filters.hasWifiUnverified || filters.hasWifiGold || filters.hasWifiSilver || filters.hasWifiBronze ? '1px solid #F59E0B' : '1px solid rgba(30, 64, 175, 0.1)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}
                              >
                                📶 WiFi
                              </motion.span>
                            )}

                            {venue.Charging_Ports > 0 && (
                              <motion.span
                                whileHover={{ scale: 1.05 }}
                                style={{
                                  padding: screenSize.isMobile ? '6px 10px' : '4px 8px', // ✅ INCREASED: Padding on mobile
                                  backgroundColor: filters.hasCharging ? '#FEF3C7' : '#DCFCE7',
                                  color: filters.hasCharging ? '#F59E0B' : '#166534',
                                  fontSize: screenSize.isMobile ? '11px' : '9px', // ✅ INCREASED: From 9px to 11px on mobile
                                  borderRadius: '10px',
                                  fontWeight: '600',
                                  textAlign: 'center',
                                  border: filters.hasCharging ? '1px solid #F59E0B' : '1px solid rgba(22, 101, 52, 0.1)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}
                              >
                                🔌 {venue.Charging_Ports}
                              </motion.span>
                            )}

                            {venue.Noise_Level === 'Quiet' && (
                              <motion.span
                                whileHover={{ scale: 1.05 }}
                                style={{
                                  padding: screenSize.isMobile ? '6px 10px' : '4px 8px', // ✅ INCREASED: Padding on mobile
                                  backgroundColor: filters.isQuiet ? '#FEF3C7' : '#F3E8FF',
                                  color: filters.isQuiet ? '#F59E0B' : '#7C3AED',
                                  fontSize: screenSize.isMobile ? '11px' : '9px', // ✅ INCREASED: From 9px to 11px on mobile
                                  borderRadius: '10px',
                                  fontWeight: '600',
                                  textAlign: 'center',
                                  border: filters.isQuiet ? '1px solid #F59E0B' : '1px solid rgba(124, 58, 237, 0.1)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}
                              >
                                🤫 Quiet
                              </motion.span>
                            )}

                            {isMosque && (
                              <motion.span
                                whileHover={{ scale: 1.05 }}
                                style={{
                                  padding: screenSize.isMobile ? '6px 10px' : '4px 8px', // ✅ INCREASED: Padding on mobile
                                  backgroundColor: filters.Mosque ? '#FEF3C7' : '#EBF8FF',
                                  color: filters.Mosque ? '#F59E0B' : '#2563eb',
                                  fontSize: screenSize.isMobile ? '11px' : '9px', // ✅ INCREASED: From 9px to 11px on mobile
                                  borderRadius: '10px',
                                  fontWeight: '600',
                                  textAlign: 'center',
                                  border: filters.Mosque ? '1px solid #F59E0B' : '1px solid rgba(37, 99, 235, 0.1)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}
                              >
                                🕌 MOSQUE
                              </motion.span>
                            )}
                          </div>
                        </div>

                        {/* Selection overlay */}
                        <motion.div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: selectedVenue === venueId
                              ? 'linear-gradient(135deg, rgba(217, 100, 31, 0.05) 0%, rgba(24, 74, 153, 0.05) 100%)'
                              : 'transparent',
                            borderRadius: '20px',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                        />
                      </motion.div>
                    );
                  })
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                    No venues found
                  </div>
                )}
              </div>
            </section>

            {/* <OffersCarousel offers={[]} autoPlay={true} autoPlayInterval={5000} /> */}
            <ZonesCarousel />
          <DiscoverBanner onClick={handleDiscoverClick} />
          <AllEventsBanner onClick={handleAllEventsClick} />
        </>
      )}
          </div>
  )}
        </DraggableCard>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .venue-cards-container::-webkit-scrollbar {
          height: 6px;
        }

        .venue-cards-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        .venue-cards-container::-webkit-scrollbar-thumb {
          background: #D9641F;
          border-radius: 3px;
        }

        .venue-cards-container::-webkit-scrollbar-thumb:hover {
          background: #B8541A;
        }
      `}</style>
    </div>
  );
};

export default VenueMap;